// src/contexts/AppContext.js - Context 리렌더링 최적화 완료
// 🎯 [Stage 2 수정] props로 environment 받기, getEnvironment() 호출 제거
// 🎯 [Stage 3 수정] 모든 API 호출에 environment 파라미터 추가

import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getTierByScore, getSubscriptionType, getSubscriptionBenefits, getAdWatchPpPoints } from '../config';
import {
  approvePayment,
  completePayment,
  getAPIUrls, // 🎯 [Fix] fetch에 URL 전달용 (verifyRewardedAd, purchaseSubscriptionWithStar)
  // [통합] 구독 관련 API - 추후 백엔드 수정 시 하나로 통합 예정
  approveAdFreePayment,
  completeAdFreePayment,
  approveVipPayment,
  completeVipPayment,
  resetAdFreeStatus as resetAdFreeAPI,
  resetVipStatus as resetVipAPI,
  updatePpPoints,
  updatePpPointsBatch,
  consumeTicket, // [추가]
  syncLandmarkCount, // 🎯 [Auto-Fix] 랜드마크 개수 동기화
  purchaseStarWithPi, // 🎯 [STAR 구매] STAR 구매 완료 API
  cancelPayment,
  recoverPendingPayment, // 🎯 [결제 복구] 출금됐는데 앱 미완료 → Pi에서 txid 조회 후 완료 처리
  purchaseSubscriptionWithStar, // [New] STAR 구독 구매 (함수는 apiCall용, URL은 getAPIUrls()에서)
  verifyRewardedAd, // [New] 광고 리워드 검증 (함수는 apiCall용, URL은 getAPIUrls()에서)
  purchaseMyLandSlot as purchaseMyLandSlotApi,
  registerMyLand,
  myLandCapture,
  renameMyLand
} from '../services/api';
// [추가] Firestore 직접 읽기를 위한 import
import { db, functions } from '../firebase';
import { bearerHeaders } from '../services/authHeader';
import { doc, getDoc, increment, setDoc, serverTimestamp, runTransaction, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions'; // [필요] purchaseTicket에서 사용
import imageLoader from '../services/imageLoader';
// Analytics 제거됨 - Firebase Analytics 사용 안함
// 🎯 [Stage 2 수정] getEnvironment 제거, 다른 유틸만 import
import { debugLog, errorLog, warnLog, maskSensitiveData, hasTestAccess } from '../utils/environment';
import bgmManager from '../services/bgmManager';

import { getEmergencyStatus, getConfigBoolean } from '../services/remoteConfig';
import { getAdWatchSpotRewards } from '../config';
import { APP_MODE } from '../constants/AppModes'; // 🎯 [New] App Modes
import PiAdapter from '../adapters/PiAdapter'; // 🎯 [NEW] Adapter Import
import { SpotService } from '../services/SpotService'; // 🎯 [Add] For point spot rewards

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export { APP_MODE }; // 🎯 Export for consumers

function mapStoredAdFreeType(rawType) {
  if (rawType === '1d' || rawType === '24h') return '1d';
  if (rawType === '7d') return '7d';
  if (rawType === '30d' || rawType === 'premium') return '30d';
  return null;
}

// 🎯 [Stage 2 핵심 수정] props에 environment와 piUser 추가
export const AppProvider = ({ children, environment: propEnvironment, piUser, authState }) => {
  const { i18n } = useTranslation();

  // 🎯 [근본 해결] 로그인 초기화 상태 확인
  const isAuthInitializing = useMemo(() => authState === 'initializing', [authState]);

  // 🎯 [Fix] authState를 context에 제공 (TosAgreementModal에서 사용)
  const contextAuthState = authState;

  /**
   * 🎯 용어 정리 (2025-08-12)
   * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   * 
   * 1. environment (앱 데이터 환경)
   *    - 'production': 메인넷 데이터 사용
   *    - 'testnet': 테스트넷 데이터 사용
   *    - 'sandbox': 개발/테스트 데이터 사용
   *    - Firebase 컬렉션과 API 엔드포인트 결정에 사용
   * 
   * 2. piSandboxMode (Pi SDK 실행 환경) - index.js에서 관리
   *    - true: PC 샌드박스에서 실행 중 (iframe 내부)
   *    - false: 실제 Pi 환경에서 실행 중
   *    - Pi SDK init()의 sandbox 플래그 결정에만 사용
   * 
   * 3. platform (실행 플랫폼)
   *    - 'pi': Pi Browser에서 실행
   *    - 'web': 일반 웹 브라우저에서 실행
   *    - 로그인 방식과 기능 제한 결정에 사용
   * 
   * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   * 핵심: environment와 piSandboxMode는 완전히 독립적인 개념
   * - environment는 "어떤 데이터를 사용할지"
   * - piSandboxMode는 "Pi SDK를 어떻게 초기화할지"
   * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   */

  // 🎯 [Stage 2 핵심 수정] props로 받은 environment 사용
  const environment = useMemo(() => {
    debugLog('[AppContext] 🎯 Stage 2: props로 받은 환경:', propEnvironment);
    return propEnvironment || 'sandbox'; // fallback
  }, [propEnvironment]);

  // [🎯 최적화] 환경별 컬렉션 경로 헬퍼 함수 - useCallback으로 메모이제이션
  const getCollectionPath = useCallback((baseName, env) => {
    if (!env || env === 'production' || env === 'mainnet') return baseName;
    // 🎯 [Fix] Sandbox uses Testnet DB for Development Consistency
    if (env === 'sandbox' || env === 'testnet') return `testnet_${baseName}`;
    return `dev_${baseName}`;
  }, []);

  // [🎯 최적화] 환경별 키들을 useMemo로 메모이제이션
  const storageKeys = useMemo(() => {
    debugLog('[AppContext] Storage keys 생성 (메모이제이션):', environment);
    return {
      PURCHASED_THEMES_KEY: `painori-purchased-themes-${environment}`,
      HONOR_SCORE_KEY: `painori-total-honor-score-${environment}`,
      SOUND_KEY: 'painori-sound-enabled', // 사운드 설정은 공통
      CLEAR_COUNT_KEY: `painori-clear-count-${environment}`,
      THEME_HIGH_SCORES_KEY: `painori-theme-high-scores-${environment}`
    };
  }, [environment]);

  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKeys.SOUND_KEY);
      return saved === null ? true : saved === 'true';
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (isSoundEnabled):', e.message);
      return true; // 기본값
    }
  });

  // 🎯 [추가] 진동 설정 상태 관리
  const [isVibrationEnabled, setIsVibrationEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('painori-vibration-enabled');
      const defaultValue = true; // 기본값: 활성화
      if (saved === null) {
        // 저장된 값이 없으면 기본값으로 설정하고 localStorage에 저장
        try { localStorage.setItem('painori-vibration-enabled', defaultValue.toString()); } catch (e) { /* ignore */ }
        return defaultValue;
      }
      return saved === 'true';
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (isVibrationEnabled):', e.message);
      return true; // 기본값
    }
  });

  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const [totalHonorScore, setTotalHonorScore] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKeys.HONOR_SCORE_KEY);
      return saved ? parseInt(saved) : 0;
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (totalHonorScore):', e.message);
      return 0; // 기본값
    }
  });

  // [수정] 총 클리어 횟수로 변경
  const [totalClearCount, setTotalClearCount] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKeys.CLEAR_COUNT_KEY);
      return saved ? parseInt(saved) : 0;
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (totalClearCount):', e.message);
      return 0; // 기본값
    }
  });

  const [purchasedThemes, setPurchasedThemes] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKeys.PURCHASED_THEMES_KEY);
      const initial = saved ? JSON.parse(saved) : {};
      debugLog('[AppContext] purchasedThemes 초기값:', Object.keys(initial));
      return initial;
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (purchasedThemes):', e.message);
      return {}; // 기본값
    }
  });

  // [추가] 티켓 시스템 상태
  const [ticketCount, setTicketCount] = useState(() => {
    try {
      const saved = localStorage.getItem(`painori-ticket-count-${environment}`);
      return saved ? parseInt(saved) : 0;
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (ticketCount):', e.message);
      return 0; // 기본값
    }
  });

  // [추가] PP 포인트 시스템 상태
  const [ppPoints, setPpPoints] = useState(() => {
    try {
      const key = `painori-pp-points-${environment}`;
      const saved = localStorage.getItem(key);
      const points = saved ? parseInt(saved) : 0;

      // 🔍 [디버깅] STAR 포인트 로딩 상태 확인
      debugLog('[AppContext] STAR 포인트 초기화:', {
        environment,
        key,
        saved,
        points,
        allKeys: Object.keys(localStorage).filter(k => k.includes('pp-points'))
      });

      return points;
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (ppPoints):', e.message);
      return 0; // 기본값
    }
  });

  // 🆕 [2026-01-29] 주간 랭킹 시스템 상태
  const [gloryPoints, setGloryPoints] = useState(0);
  const [weeklyScore, setWeeklyScore] = useState(0);

  // [New] SPOT 포인트 상태 (전역 관리)
  const [spotPoints, setSpotPoints] = useState(() => {
    try {
      const key = `painori-spot-points-${environment}`;
      const saved = localStorage.getItem(key);
      return saved ? parseFloat(saved) : 0;
    } catch (e) {
      console.warn('[AppContext] localStorage failure (spotPoints):', e);
      return 0;
    }
  });

  // My Land: 슬롯 구매 상태·등록 위치 (onSnapshot 동기화)
  const [myLandSlots, setMyLandSlots] = useState({});
  const [myLandRegistrations, setMyLandRegistrations] = useState({});

  // 🆕 [GiftBox] 선물상자 인벤토리 상태 (onSnapshot 동기화)
  const [pointSpotInventory, setPointSpotInventory] = useState(() => {
    try {
      const key = `painori-giftbox-inv-${environment}`;
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : { date: null, normal: 0, landmark: 0 };
    } catch (e) {
      return { date: null, normal: 0, landmark: 0 };
    }
  });

  // 🆕 [GiftBox] 오늘 오픈한 상자 수 (일일 제한 확인용)
  const [pointSpotOpenedToday, setPointSpotOpenedToday] = useState(() => {
    try {
      const key = `painori-giftbox-opened-${environment}`;
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : { normal: 0, landmark: 0 };
    } catch (e) {
      return { normal: 0, landmark: 0 };
    }
  });

  // [추가] Pi Clash 일일 게임 제한 상태

  // [신규] 테마별 최고 점수 상태
  const [themeHighScores, setThemeHighScores] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKeys.THEME_HIGH_SCORES_KEY);
      const initial = saved ? JSON.parse(saved) : {};
      debugLog('[AppContext] themeHighScores 초기값:', initial);
      return initial;
    } catch (e) {
      console.warn('[AppContext] localStorage 접근 실패 (themeHighScores):', e.message);
      return {}; // 기본값
    }
  });

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const subscriptionPurchaseInFlightRef = useRef(false);
  const [isPaymentEnabled, setIsPaymentEnabled] = useState(true);
  const [currentThemePage, setCurrentThemePage] = useState(0);

  // 🆕 [2025-12-19 추가] 테스트 모드 상태 관리
  const [isTestMode, setIsTestMode] = useState(false);

  // [통합] 구독 상태 관리 - adFreeStatus와 vipStatus를 하나로 통합
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // [🔧 수정] Firebase 업데이트 디바운스를 위한 타이머 - 더 이상 사용하지 않음
  const updateStatsTimerRef = React.useRef(null);

  // [추가] 초기 동기화 에러 상태
  const [initialSyncError, setInitialSyncError] = useState(false);

  // [추가] GPS 권한 상태 (DB 로드값)
  // undefined: 로딩 전, null: 데이터 없음(거부/미설정), object: 설정됨
  const [savedGpsPermission, setSavedGpsPermission] = useState(undefined);

  // 🎯 [ToS] 약관 동의 상태 (DB 로드값)
  // undefined: 로딩 전, null: 동의 안함, object: 동의함
  const [savedAgreements, setSavedAgreements] = useState(undefined);

  // 🎯 [AppMode] 전역 리소스 관리 (Set 기반 Reason Locking)
  const [appMode, setAppMode] = useState(APP_MODE.NORMAL);
  const heavyTaskReasons = useRef(new Set()); // 🎯 [Locking] 원인별 상태 관리

  const setHeavyTaskState = useCallback((isActive, reason = 'unknown') => {
    if (isActive) {
      // 1. 활성화 요청: 원인 추가
      heavyTaskReasons.current.add(reason);
      setAppMode(APP_MODE.HEAVY_TASK);
      debugLog(`[AppContext] AppMode: HEAVY_TASK (Reason Added: ${reason})`, [...heavyTaskReasons.current]);
    } else {
      // 2. 비활성화 요청: 원인 제거
      heavyTaskReasons.current.delete(reason);

      // 3. 남은 원인이 없으면 NORMAL 복구
      if (heavyTaskReasons.current.size === 0) {
        setAppMode(APP_MODE.NORMAL);
        debugLog('[AppContext] AppMode: NORMAL (All Reasons Cleared)');
      } else {
        // 아직 다른 원인이 남아있음 -> HEAVY 유지
        debugLog(`[AppContext] AppMode: HEAVY Maintained (Remaining: ${[...heavyTaskReasons.current]})`);
      }
    }
  }, []);

  // [추가] 앱 가시성 변경 감지 (백그라운드/포그라운드 전환)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;

      if (isVisible) {
        // [포그라운드 복귀] 백그라운드 태그 제거
        setHeavyTaskState(false, 'background_mode');
        debugLog('[AppContext] 앱 포커스 복귀: background_mode 해제');
      } else {
        // [백그라운드 진입] 백그라운드 태그 추가 -> HEAVY_TASK 강제
        setHeavyTaskState(true, 'background_mode');
        debugLog('[AppContext] 앱 백그라운드 진입: HEAVY_TASK 전환 (background_mode)');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setHeavyTaskState]);
  // 🎯 [Landmark v4.1] SPOT 지급률 상태
  const [landmarkSpotRate, setLandmarkSpotRate] = useState(null); // DB에서 로드 전까지 null

  // 🎯 [Landmark v4.1] Landmark Occupancy Limit (hours)
  const [landmarkOccupancyLimit, setLandmarkOccupancyLimit] = useState(24); // Default 24h

  // 🎯 [Landmark v4.1] SPOT rate config — getDoc + 30분 폴링 (비용 최적화)
  useEffect(() => {
    if (!environment) return;

    const configCollectionPath = getCollectionPath('config', environment);
    const spotRateDocRef = doc(db, configCollectionPath, 'landmark_spot_rate');

    const loadSpotRate = async () => {
      try {
        const snapshot = await getDoc(spotRateDocRef);
        if (snapshot.exists() && snapshot.data()?.value !== undefined) {
          const data = snapshot.data();
          const rate = data.value;
          const limit = data.occupation_time_hours || 24; // Default if missing
          setLandmarkSpotRate(rate);
          setLandmarkOccupancyLimit(limit);
          debugLog('[AppContext] ✅ Landmark SPOT rate loaded:', rate, 'Limit:', limit);
        } else {
          errorLog('[AppContext] ⚠️ CRITICAL: landmark_spot_rate config not found in DB!');
          errorLog('[AppContext] Please run: node scripts/init_landmark_spot_rate.js');
          setLandmarkSpotRate(null); // DB 값 없음 명시
        }
      } catch (error) {
        errorLog('[AppContext] ❌ Failed to load SPOT rate:', error);
        setLandmarkSpotRate(null); // 읽기 실패
      }
    };

    // 초기 1회 로드
    loadSpotRate();

    // 30분마다 재확인 (관리자 설정 변경 빈도 극히 낮음)
    const rateInterval = setInterval(loadSpotRate, 30 * 60 * 1000);

    return () => clearInterval(rateInterval);
  }, [environment, getCollectionPath]);

  // 🎯 [SYNC] User Data Sync — getDoc + 5분 폴링 (비용 최적화)
  // [2026-04-07] onSnapshot → getDoc 전환. API 응답으로 로컬 즉시 갱신은 기존대로 유지.
  useEffect(() => {
    if (!piUser?.uid) {
      // 🎯 [핵심] piUser 없으면 GPS 권한도 null로 초기화
      setSavedGpsPermission(null);
      return;
    }

    const userCollectionPath = getCollectionPath('users', environment);
    const userDocRef = doc(db, userCollectionPath, piUser.uid);

    debugLog('[AppContext] 📡 Starting User Sync (getDoc + polling) for:', piUser.uid);

    // 📦 동기화 처리 함수 (기존 onSnapshot 콜백과 동일)
    const syncUserData = (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        debugLog('[AppContext] 📥 User Data Received:', data);

        // 🚨 [Moderation] 정지된 유저 체크 (강제 로그아웃)
        if (data.reportHistory?.userStatus === 'banned') {
          debugLog('[AppContext] 🚫 정지된 사용자 감지됨! 강제 로그아웃 진행');
          window.dispatchEvent(new CustomEvent('force-guest-mode', {
            detail: { reason: data.reportHistory.bannedReason }
          }));
          return; // 상태 업데이트 중단
        }

        // Sync Tickets
        if (data.ticketCount !== undefined) {
          setTicketCount(data.ticketCount);
          localStorage.setItem(`painori-ticket-count-${environment}`, data.ticketCount);
        }

        // Sync PP Points
        if (data.ppPoints !== undefined) {
          setPpPoints(data.ppPoints);
          localStorage.setItem(`painori-pp-points-${environment}`, data.ppPoints);
          // 💰 [비용 최적화] 서버 값으로 방금 맞췄으므로 앱 복귀 시 sync_check(서버 재확인)를 생략한다.
          // 2시간 넘게 못 맞춘 경우에는 setupEventBasedSync가 예전처럼 서버에 확인한다.
          window.lastPpSyncTime = Date.now();
        }

        // [New] Sync SPOT Points
        if (data.spotPoints !== undefined) {
          setSpotPoints(data.spotPoints);
          localStorage.setItem(`painori-spot-points-${environment}`, data.spotPoints);
        }

        // 🆕 [GiftBox] Sync Inventory
        if (data.pointSpotInventory !== undefined) {
          setPointSpotInventory(data.pointSpotInventory);
          localStorage.setItem(`painori-giftbox-inv-${environment}`, JSON.stringify(data.pointSpotInventory));
        }

        // 🆕 [GiftBox] Sync Opened Today
        if (data.pointSpotOpenedToday !== undefined) {
          setPointSpotOpenedToday(data.pointSpotOpenedToday);
          localStorage.setItem(`painori-giftbox-opened-${environment}`, JSON.stringify(data.pointSpotOpenedToday));
        }

        // 🆕 [2026-01-29] Sync Glory Points & Weekly Score
        if (data.gloryPoints !== undefined) {
          setGloryPoints(data.gloryPoints);
        }
        if (data.weeklyScore !== undefined) {
          setWeeklyScore(data.weeklyScore);
        }

        // My Land: 슬롯·등록 위치 동기화
        if (data.myLandSlots !== undefined) {
          setMyLandSlots(data.myLandSlots);
        }
        if (data.myLandRegistrations !== undefined) {
          setMyLandRegistrations(data.myLandRegistrations);
        }

        // 🎯 [핵심 수정] GPS Permission도 함께 동기화
        // 로그인 시점에 한 번에 로드되므로 타이밍 문제 해결
        if (data.gpsPermission !== undefined) {
          setSavedGpsPermission(data.gpsPermission);
          debugLog('[AppContext] 📍 GPS 권한 로드 완료:', data.gpsPermission);
        } else {
          // DB에 gpsPermission 필드 없으면 null (새 사용자)
          setSavedGpsPermission(null);
          debugLog('[AppContext] 📍 GPS 권한 없음 (새 사용자)');
        }

        // 🎯 [ToS] 약관 동의 정보 로드
        if (data.agreements !== undefined) {
          setSavedAgreements(data.agreements);
          debugLog('[AppContext] 📋 약관 동의 로드 완료:', data.agreements);
        } else {
          setSavedAgreements(null);
          debugLog('[AppContext] 📋 약관 동의 없음 (새 사용자)');
        }

        // 🎯 [Auto-Fix] 랜드마크 개수 동기화 및 초과 점유 자동 정리
        // 3개 초과인 경우에만 서버에 요청 (비용 최적화)
        // 서버(syncLandmarkCount)는 개수가 없거나(null/undefined) 3개 이하이면 아무것도 바꾸지 않고 끝내므로 부르지 않는다.
        const currentLandCount = data.currentLandCount;
        if (currentLandCount > 3) {
          // 세션당 1회만 호출하도록 체크 (localStorage 사용)
          const syncKey = `landmark-sync-${piUser.uid}-${environment}`;
          const lastSyncTime = localStorage.getItem(syncKey);
          const now = Date.now();
          const fourHours = 4 * 60 * 60 * 1000; // 4시간 (비정상 상태 보정 — 빈도 낮춤)

          // 마지막 동기화가 4시간 이내면 스킵 (비용 최적화)
          if (!lastSyncTime || (now - parseInt(lastSyncTime)) > fourHours) {
            debugLog('[AppContext] 🎯 랜드마크 개수 동기화 필요:', { currentLandCount });
            syncLandmarkCount(piUser, environment)
              .then((result) => {
                if (result?.success) {
                  debugLog('[AppContext] ✅ 랜드마크 개수 동기화 완료:', result);
                  localStorage.setItem(syncKey, now.toString());
                }
              })
              .catch((error) => {
                errorLog('[AppContext] ❌ 랜드마크 개수 동기화 실패:', error);
              });
          } else {
            debugLog('[AppContext] ⏭️ 랜드마크 개수 동기화 스킵 (최근 실행됨)');
          }
        }
      } else {
        // 문서 자체가 없으면 새 사용자
        setSavedGpsPermission(null);
        setSavedAgreements(null); // 🎯 [Fix] 새 사용자 ToS 모달 표시를 위해 추가
        debugLog('[AppContext] 📍 사용자 문서 없음 - GPS/약관 null');
      }
    };

    // 화면이 보일 때만 5분마다 한 번 읽는다. 정지 확인은 이 동기화에 포함된다.
    const SYNC_MS = 5 * 60 * 1000;
    let lastSyncAt = 0;
    let hasSynced = false;
    const runSync = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const first = !hasSynced;
      hasSynced = true;
      lastSyncAt = Date.now();
      try {
        const docSnap = await getDoc(userDocRef);
        syncUserData(docSnap);
      } catch (error) {
        console.error("[AppContext] User Sync Error:", error);
        if (first) setSavedGpsPermission(null);
      }
    };
    runSync();

    const syncInterval = setInterval(runSync, SYNC_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastSyncAt >= SYNC_MS) runSync();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(syncInterval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [piUser?.uid, environment, getCollectionPath]);

  useEffect(() => {
    if (!piUser?.uid) return undefined;
    const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const day = kst.toISOString().slice(0, 10);
    const storageKey = `user-day-${environment}-${piUser.uid}-${day}`;
    try {
      if (localStorage.getItem(storageKey)) return undefined;
    } catch (e) {
      return undefined;
    }
    const dayId = `${piUser.uid}_${day}`;
    const ref = doc(db, getCollectionPath('user_days', environment), dayId);
    setDoc(ref, {
      userId: piUser.uid,
      date: day,
      createdAt: serverTimestamp()
    }).then(() => {
      try { localStorage.setItem(storageKey, '1'); } catch (e) { /* ignore */ }
    }).catch(() => {});
    return undefined;
  }, [piUser?.uid, environment, getCollectionPath]);

  // [🎯 최적화] 중복 호출 방지를 위한 플래그들을 useRef로 관리
  const isCheckingSubscriptionRef = React.useRef(false);
  const lastCheckTimeRef = React.useRef(0);

  // [🎯 최적화] 게스트 여부 확인 헬퍼 함수 - useCallback으로 메모이제이션
  const isGuest = useCallback(() => {
    if (authState !== 'authenticated') return true;
    const userType = sessionStorage.getItem('user-type');
    return userType === 'guest';
  }, [authState]);

  // 🆕 [2025-12-19 추가] 테스트 모드 접근 권한 확인
  useEffect(() => {
    const testMode = hasTestAccess();
    setIsTestMode(testMode);
    debugLog('[AppContext] 테스트 모드 상태:', testMode);
  }, []);

  // 🆕 [2025-12-19 추가] 결제 가능 여부 확인 함수 - 테스트 모드 고려
  const canMakePayment = useCallback(() => {
    if (authState !== 'authenticated') return false;
    // 테스트 모드이면 테스트용 결제 설정 확인
    if (isTestMode) {
      const testPaymentEnabled = getConfigBoolean('test_payment_enabled', false);
      debugLog('[AppContext] 테스트 모드 결제 허용:', testPaymentEnabled);
      return testPaymentEnabled;
    }
    // 일반 모드이면 기존 결제 설정 확인
    return isPaymentEnabled;
  }, [authState, isTestMode, isPaymentEnabled]);


  // 🎯 [제거됨] GPS 권한 로딩은 이제 onSnapshot에서 처리 (Line 191-241)
  // 이전 코드는 타이밍 문제를 일으켰으므로 삭제함


  // \ud83c\udfaf [\uacb0\uc81c \ubcf5\uad6c] \uc571 \uc2dc\uc791 \uc2dc sessionStorage\uc5d0 \ubbf8\uc644\ub8cc \uacb0\uc81c\uac00 \ubc1c\uacac\ub418\uba74 \uc790\ub3d9 \ubcf5\uad6c \uc2dc\ub3c4
  // onIncompletePaymentFound\uac00 \uba38\uc800 \uc2e4\ud589\ub418\uc9c0 \uc54a\uc558\uac70\ub098 \ub124\ud2b8\uc6cc\ud06c \uc624\ub958\ub85c \uc2e4\ud328\ud55c \uacbd\uc6b0\uc5d0 \ub300\ud55c \ubcf4\uc870 \uccb4\uc778
  useEffect(() => {
    const checkIncompletePayment = async () => {
      if (isCheckingIncompleteRef.current) {
        return;
      }
      isCheckingIncompleteRef.current = true;

      const incompletePayment = sessionStorage.getItem('pi-incomplete-payment');
      if (incompletePayment) {
        debugLog('[AppContext] \ubbf8\uc644\ub8cc \uacb0\uc81c \ubc1c\uacac - \uc11c\ubc84 \ubcf5\uad6c \uc2dc\ub3c4');

        try {
          const paymentData = JSON.parse(incompletePayment);
          const paymentId = paymentData.identifier;
          // txid\uac00 \uc800\uc7a5\ub418\uc5b4 \uc788\uc73c\uba74 Pi API \uc7ac\uc870\ud68c \uc5c6\uc774 \uc9c1\uc811 complete \ud638\ucd9c (onIncompletePaymentFound\uc5d0\uc11c \uc800\uc7a5)
          const txid = paymentData.txid || (paymentData.transaction?.txid);

          if (paymentId) {
            debugLog('[AppContext] \ubbf8\uc644\ub8cc \uacb0\uc81c \ubcf5\uad6c \uc694\uccad:', { paymentId, hasTxid: !!txid });

            let recovered = false;

            if (txid) {
              // txid\uac00 \uc788\uc73c\uba74 \uc9c1\uc811 complete (Pi API \uc7ac\uc870\ud68c \ubd88\ud544\uc694)
              await completePayment(paymentId, txid, environment);
              recovered = true;
              debugLog('[AppContext] \u2705 txid \uc9c1\uc811 complete \uc131\uacf5:', paymentId);
            } else {
              // txid \uc5c6\uc73c\uba74 \uc11c\ubc84\uc5d0\uc11c Pi API \uc870\ud68c \ud6c4 complete \uc2dc\ub3c4
              const recoverResult = await recoverPendingPayment(paymentId, environment);
              if (recoverResult && recoverResult.recovered) {
                recovered = true;
                debugLog('[AppContext] \u2705 recoverPendingPayment \uc131\uacf5:', paymentId);
              } else {
                // \ubí확\uc815 \uc0c1\ud0dc(\uc544\uc9c1 \ube14\ub85d\uccb4\uc778 \ubbf8\ub4f1\ub85d)\uc774\ubbc0\ub85c \ucde8\uc18c
                debugLog('[AppContext] \ubcf5\uad6c \ubd88\uac00(\ud655\uc815 txid \uc5c6\uc74c) \u2192 \ucde8\uc18c \uc2dc\ub3c4:', paymentId);
                await cancelPayment(paymentId, environment);
                debugLog('[AppContext] \u2705 cancelPayment \uc131\uacf5:', paymentId);
              }
            }

            // \ubcf5\uad6c/\ucde8\uc18c \uc131\uacf5 \ud6c4\uc5d0\ub9cc sessionStorage \uc0ad\uc81c
            sessionStorage.removeItem('pi-incomplete-payment');
            debugLog('[AppContext] \ubbf8\uc644\ub8cc \uacb0\uc81c \ucc98\ub9ac \uc644\ub8cc. \uacb0\uc81c \ucc28\ub2e8 \ud574\uc81c.');
          }

        } catch (error) {
          errorLog('[AppContext] \ubbf8\uc644\ub8cc \uacb0\uc81c \uc790\ub3d9 \ubcf5\uad6c \uc2e4\ud328:', error);
          // \u274c \uc2e4\ud328 \uc2dc sessionStorage\ub97c \uc0ad\uc81c\ud558\uc9c0 \uc54a\uc74c
          // \u2192 \ub2e4\uc74c \uc0c8\ub85c\uace0\uce68(reload)\uc774\ub098 \ub2e4\uc2dc \uc811\uc18d\ud560 \ub54c onIncompletePaymentFound\uac00 \uc7ac\uc2dc\ub3c4 \uac00\ub2a5\ud558\ub3c4\ub85d \ubcf4\uc874
          // (reload() \uc81c\uac70: reload\ub294 \ubb34\ud55c \ub8e8\ud504 \uc704\ud5d8\uc774 \uc788\uc74c)
        } finally {
          isCheckingIncompleteRef.current = false;
        }
      } else {
        isCheckingIncompleteRef.current = false;
      }
    };

    checkIncompletePayment();
  }, [environment]);


  useEffect(() => {
    const pendingPaymentKey = `painori-pending-payment-${environment}`;
    const pendingPayment = localStorage.getItem(pendingPaymentKey);
    if (pendingPayment) {
      const pending = JSON.parse(pendingPayment);
      const age = Date.now() - pending.timestamp;

      if (age > 24 * 60 * 60 * 1000) {
        debugLog('[AppContext] 만료된 결제 정보 제거');
        localStorage.removeItem(pendingPaymentKey);
      } else {
        debugLog('[AppContext] 진행 중인 결제 발견');
      }
    }
  }, [environment]);

  useEffect(() => {
    localStorage.setItem(storageKeys.SOUND_KEY, isSoundEnabled);
  }, [isSoundEnabled, storageKeys.SOUND_KEY]);

  useEffect(() => {
    localStorage.setItem(storageKeys.HONOR_SCORE_KEY, totalHonorScore.toString());
  }, [totalHonorScore, storageKeys.HONOR_SCORE_KEY]);

  // [수정] 총 클리어 횟수 저장
  useEffect(() => {
    localStorage.setItem(storageKeys.CLEAR_COUNT_KEY, totalClearCount.toString());
  }, [totalClearCount, storageKeys.CLEAR_COUNT_KEY]);

  useEffect(() => {
    localStorage.setItem(storageKeys.PURCHASED_THEMES_KEY, JSON.stringify(purchasedThemes));
    debugLog('[AppContext] purchasedThemes 상태 변경:', Object.keys(purchasedThemes));
  }, [purchasedThemes, storageKeys.PURCHASED_THEMES_KEY]);

  // [신규] 테마별 최고 점수 저장
  useEffect(() => {
    localStorage.setItem(storageKeys.THEME_HIGH_SCORES_KEY, JSON.stringify(themeHighScores));
    debugLog('[AppContext] themeHighScores 상태 변경:', themeHighScores);
  }, [themeHighScores, storageKeys.THEME_HIGH_SCORES_KEY]);

  // [🎯 최적화] 구독 상태 캐시 시스템
  const subscriptionCache = useRef({
    data: null,
    timestamp: 0,
    ttl: 15 * 60 * 1000 // 15분 캐시 (5분 → 15분으로 증가)
  });

  // [🎯 성능 최적화] 구독 상태 확인 캐시
  const hasActiveSubscriptionCache = useRef({
    result: null,
    timestamp: 0
  });

  // 🎯 [결제 복구] 미완료 결제 자동 처리 중복 실행 방지
  const isCheckingIncompleteRef = useRef(false);

  // 🎯 [Helper] 인증 상태 확인
  const checkAuth = useCallback(() => {
    if (!piUser?.uid) {
      errorLog('[AppContext] User not authenticated.');
      return false;
    }
    return true;
  }, [piUser?.uid]);

  // 🎯 [Optimization] PP 포인트 업데이트 함수 (Firestore Transaction)
  const updatePpPointsInternal = useCallback(async (amount, reason, relatedId = null) => {
    if (!checkAuth()) return false;

    // 🎯 [Optimization] 중복 호출 방지 (2초 내 동일 요청 무시)
    const now = Date.now();
    if (lockState.current.updatingPoints && (now - lockState.current.lastPointsUpdate < 2000)) {
      console.warn(`[AppContext] updatePpPoints 중복 호출 방지됨: ${reason}`);
      return false;
    }

    lockState.current.updatingPoints = true;
    lockState.current.lastPointsUpdate = now;

    try {
      const pointsCol = getCollectionPath('users', environment); // 🎯 [Fix] users 컬렉션 사용
      const userPointsRef = doc(db, pointsCol, piUser.uid);

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userPointsRef);
        if (!userDoc.exists()) {
          throw new Error('User document does not exist.');
        }

        const currentPpPoints = userDoc.data().ppPoints || 0;
        const newPpPoints = currentPpPoints + amount;

        if (newPpPoints < 0) {
          throw new Error('Insufficient PP points');
        }

        transaction.update(userPointsRef, {
          ppPoints: newPpPoints,
          lastPpUpdate: serverTimestamp(),
          [`ppHistory.${Date.now()}`]: { amount, reason, relatedId, timestamp: serverTimestamp() }
        });
      });
      // 🎯 [Note] 성공 후 별도 상태 업데이트 불필요 (onSnapshot이 처리)
      return true;
    } catch (error) {
      console.error('Error updating points:', error);
      return false;
    } finally {
      // 락 해제 (약간의 지연을 두어 더블클릭 방지)
      setTimeout(() => {
        lockState.current.updatingPoints = false;
      }, 500);
    }
  }, [checkAuth, environment, piUser, getCollectionPath]);


  // [🎯 최적화] Firestore에서 사용자 구독 상태 직접 읽기 - 캐시 시스템 도입
  const checkSubscriptionStatusFromFirestore = useCallback(async (userId) => {
    try {
      const now = Date.now();

      // 캐시 유효성 검사
      if (subscriptionCache.current.data &&
        (now - subscriptionCache.current.timestamp) < subscriptionCache.current.ttl) {
        debugLog('[AppContext] ✅ 캐시된 구독 상태 사용 (Firestore 읽기 0회)');
        return subscriptionCache.current.data;
      }

      debugLog('[AppContext] 🎯 비용 최적화: Firestore에서 구독 상태 직접 확인 시작');

      // 사용자 문서 경로 구성
      const userCollectionPath = getCollectionPath('users', environment);
      const userDocRef = doc(db, userCollectionPath, userId);

      debugLog('[AppContext] 사용자 문서 경로:', `${userCollectionPath}/${userId}`);

      // Firestore에서 사용자 문서 직접 읽기
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        debugLog('[AppContext] 사용자 문서 없음 - 기본값 반환');
        return {
          adFree: null,
          vip: null,
          purchasedThemes: [],
          totalScore: 0,
          totalClearCount: 0,
          currentRank: 'Iron',
          themeHighScores: {},
          ppPoints: 0 // [추가] STAR 포인트 기본값
        };
      }

      const userData = userDocSnap.data();

      debugLog('[AppContext] Firestore에서 사용자 데이터 읽기 성공');

      // 광고 제거 상태 확인 (기존 로직 유지)
      let adFreeStatus = null;
      if (userData.adFree && userData.adFree.active !== false && userData.adFree.expiresAt) {
        const expiresAt = userData.adFree.expiresAt.toDate();
        if (expiresAt > new Date()) {
          adFreeStatus = {
            type: userData.adFree.type,
            expiresAt: expiresAt.toISOString()
          };
        }
      }

      // VIP 상태 확인 (기존 로직 유지)
      let vipStatus = null;
      if (userData.vip && userData.vip.active !== false && userData.vip.expiresAt) {
        const expiresAt = userData.vip.expiresAt.toDate();
        if (expiresAt > new Date()) {
          const lastReset = userData.vip.lastBonusHintReset?.toDate() || new Date(0);
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);

          let bonusHintsUsedToday = userData.vip.bonusHintsUsedToday || 0;

          if (lastReset < todayStart) {
            bonusHintsUsedToday = 0;
          }

          vipStatus = {
            active: true,
            expiresAt: expiresAt.toISOString(),
            bonusHintsUsedToday
          };
        }
      }

      const result = {
        adFree: adFreeStatus,
        vip: vipStatus,
        purchasedThemes: userData.purchasedThemes || [],
        totalScore: userData.totalScore || 0,
        totalClearCount: userData.totalClearCount || 0,
        currentRank: userData.currentRank || 'Iron',
        themeHighScores: userData.themeHighScores || {},
        ppPoints: userData.ppPoints || 0, // [추가] STAR 포인트 서버 데이터
        gpsPermission: userData.gpsPermission || null, // [추가] GPS 권한 정보
        ticketCount: userData.ticketCount || 0 // 🎯 [추가] 티켓 DB 동기화
      };

      debugLog('[AppContext] ✅ 비용 최적화 성공: Functions 호출 0회로 구독 상태 확인 완료');

      // 캐시 업데이트
      subscriptionCache.current.data = result;
      subscriptionCache.current.timestamp = now;

      return result;

    } catch (error) {
      errorLog('[AppContext] Firestore 구독 상태 확인 실패:', error);
      throw error;
    }
  }, [environment, getCollectionPath]);

  // [🎯 최적화] 구독 상태 확인 - 중복 호출 방지 로직 대폭 강화
  const checkSubscriptionStatus = useCallback(async (forceCheck = false) => {
    if (!piUser) return;

    // 🎯 [Optimization] forceCheck 스로틀링 (10초)
    const now = Date.now();
    if (forceCheck && (now - lockState.current.lastSubscriptionCheck < 10000)) {
      // console.log('[AppContext] 구독 체크 스로틀링됨');
      forceCheck = false; // 강제 체크 무시하고 캐시 사용 시도
    }
    if (forceCheck) {
      lockState.current.lastSubscriptionCheck = now;
    }

    debugLog('[AppContext] ===== checkSubscriptionStatus 시작 =====');

    // [🎯 최적화] 중복 호출 방지 - useRef 사용으로 성능 향상
    if (isCheckingSubscriptionRef.current && !forceCheck) {
      debugLog('[AppContext] 이미 구독 상태 확인 중 - 스킵 (useRef 최적화)');
      return;
    }

    // [🎯 최적화] 짧은 시간 내 중복 호출 방지 (5초 간격)
    if (!forceCheck && (now - lastCheckTimeRef.current) < 5000) {
      debugLog('[AppContext] 최근에 확인했음 - 스킵 (5초 간격 제한)');
      return;
    }

    try {
      const userId = sessionStorage.getItem('pi-user-id');
      debugLog('[AppContext] 사용자 ID:', maskSensitiveData({ userId }).userId);

      if (!userId) {
        debugLog('[AppContext] 사용자 ID 없음 - 종료');
        return;
      }

      // 🔧 [결제 오류 수정] forceCheck 시 모든 캐시 무효화
      if (forceCheck) {
        subscriptionCache.current.data = null;
        subscriptionCache.current.timestamp = 0;
        subscriptionCache.current.ttl = 0;

        // localStorage 캐시도 무효화
        const cacheKey = `subscription_${userId}_${environment}`;
        localStorage.removeItem(cacheKey);

        debugLog('[AppContext] 강제 갱신 - 모든 캐시 무효화');
      }

      // [🎯 최적화] 게스트는 구독 상태 확인 스킵 - 조건 체크를 먼저
      if (isGuest()) {
        debugLog('[AppContext] 게스트 유저 - 구독 상태 확인 스킵');
        return;
      }

      isCheckingSubscriptionRef.current = true;
      lastCheckTimeRef.current = now;

      // ✅ [2025-09-19 추가] 메인넷 환경에서 데이터 초기화 안전장치
      if (environment === 'mainnet') {
        const resetFlag = 'painori-data-reset-20250919';
        const hasReset = localStorage.getItem(resetFlag);

        if (!hasReset) {
          debugLog('[AppContext] 메인넷 데이터 초기화 실행');
          // 구매 정보와 점수 정보 모두 초기화
          setPurchasedThemes({});
          setThemeHighScores({});
          localStorage.setItem(storageKeys.PURCHASED_THEMES_KEY, JSON.stringify({}));
          localStorage.setItem(storageKeys.THEME_HIGH_SCORES_KEY, JSON.stringify({}));
          localStorage.setItem(resetFlag, 'true');

          debugLog('[AppContext] 메인넷 데이터 초기화 완료');
        }
      }

      // ✅ [2025-09-22 추가] 테마별 최고 점수 1회성 초기화 (점수 시스템 리뉴얼)
      const themeScoreResetFlag = 'theme-scores-reset-20250922';
      const hasThemeScoreReset = localStorage.getItem(themeScoreResetFlag);

      if (!hasThemeScoreReset) {
        debugLog('[AppContext] 9월 22일 기준 테마별 최고 점수 초기화 실행');
        // 🎯 [수정] 모든 테마 ID 강제 초기화 (빈 객체 문제 해결)
        const resetScores = {
          masterpiece_1: 0,
          masterpiece_2: 0,
          masterpiece_3: 0,
          masterpiece_4: 0,
          masterpiece_5: 0,
          masterpiece_6: 0
        };

        setThemeHighScores(resetScores);
        localStorage.setItem(storageKeys.THEME_HIGH_SCORES_KEY, JSON.stringify(resetScores));
        localStorage.setItem(themeScoreResetFlag, 'true');

        debugLog('[AppContext] ✅ 테마별 최고 점수 강제 초기화 완료 - 새로운 점수 시스템 적용');
      }

      // [비용 최적화] Functions API 대신 Firestore 직접 읽기
      try {
        // Remote Config에서 결제 활성화 상태 확인
        const emergencyStatus = getEmergencyStatus();
        setIsPaymentEnabled(emergencyStatus.isPaymentEnabled);
        debugLog('[AppContext] 결제 활성화 상태:', emergencyStatus.isPaymentEnabled);

        debugLog('[AppContext] 🎯 Firestore 직접 읽기 시작');
        const data = await checkSubscriptionStatusFromFirestore(userId);
        debugLog('[AppContext] Firestore 직접 읽기 응답 수신');
        debugLog('[AppContext] purchasedThemes 개수:', data.purchasedThemes?.length || 0);

        // 🎯 [근본적 해결] 게임 통계 동기화 로직 제거
        // 구독 상태 확인 함수는 구독 관련 데이터만 처리
        // 게임 통계는 별도의 동기화 함수에서 처리
        debugLog('[AppContext] 구독 상태만 동기화 - 게임 통계 동기화 제거됨');

        // [신규] 테마별 최고 점수 동기화
        if (data.themeHighScores && typeof data.themeHighScores === 'object') {
          debugLog('[AppContext] Firebase에서 테마별 최고 점수 수신:', data.themeHighScores);

          // 로컬과 서버 데이터 병합 (더 높은 점수 유지)
          const localHighScores = JSON.parse(localStorage.getItem(storageKeys.THEME_HIGH_SCORES_KEY) || '{}');
          const mergedHighScores = { ...data.themeHighScores };

          // 로컬에만 있는 점수가 더 높으면 유지
          Object.keys(localHighScores).forEach(themeId => {
            const localScore = localHighScores[themeId] || 0;
            const serverScore = mergedHighScores[themeId] || 0;
            if (localScore > serverScore) {
              mergedHighScores[themeId] = localScore;
              debugLog(`[AppContext] 테마 ${themeId}: 로컬 점수가 더 높음 (${localScore} > ${serverScore})`);
            }
          });

          setThemeHighScores(mergedHighScores);
          localStorage.setItem(storageKeys.THEME_HIGH_SCORES_KEY, JSON.stringify(mergedHighScores));
        } else {
          // ✅ [2025-09-19 추가] Firebase에 테마 점수 정보가 없으면 로컬도 초기화
          debugLog('[AppContext] Firebase에 테마 점수 없음 - 로컬 캐시 초기화');
          setThemeHighScores({});
          localStorage.setItem(storageKeys.THEME_HIGH_SCORES_KEY, JSON.stringify({}));
        }

        // [추가] Firebase에서 구매한 테마 정보 가져오기
        if (data.purchasedThemes && Array.isArray(data.purchasedThemes) && data.purchasedThemes.length > 0) {
          debugLog('[AppContext] Firebase에서 구매 테마 정보 수신:', data.purchasedThemes.length, '개');

          // 배열을 객체 형태로 변환
          const themesObject = {};
          data.purchasedThemes.forEach(themeId => {
            debugLog('[AppContext] 테마 처리 중:', themeId);
            themesObject[themeId] = {
              purchasedAt: new Date().toISOString(),
              fromFirebase: true
            };
          });

          debugLog('[AppContext] 변환된 테마 객체:', Object.keys(themesObject));

          // 현재 localStorage의 구매 정보와 병합
          const localThemes = JSON.parse(localStorage.getItem(storageKeys.PURCHASED_THEMES_KEY) || '{}');
          debugLog('[AppContext] localStorage 테마:', Object.keys(localThemes));

          const mergedThemes = { ...themesObject, ...localThemes }; // localStorage가 우선
          debugLog('[AppContext] 병합된 테마:', Object.keys(mergedThemes));

          // 상태 업데이트
          setPurchasedThemes(prevThemes => {
            debugLog('[AppContext] 이전 상태:', Object.keys(prevThemes));
            debugLog('[AppContext] 새로운 상태:', Object.keys(mergedThemes));
            return mergedThemes;
          });

          // localStorage 업데이트
          localStorage.setItem(storageKeys.PURCHASED_THEMES_KEY, JSON.stringify(mergedThemes));
          debugLog('[AppContext] localStorage 업데이트 완료');

          debugLog('[AppContext] 구매 테마 정보 동기화 완료:', Object.keys(mergedThemes).length, '개');
        } else {
          debugLog('[AppContext] purchasedThemes가 없거나 비어있음');
          // ✅ [2025-09-19 추가] Firebase에 구매 정보가 없으면 로컬 캐시도 초기화
          debugLog('[AppContext] Firebase에 구매 정보 없음 - 로컬 캐시 초기화');
          setPurchasedThemes({});
          localStorage.setItem(storageKeys.PURCHASED_THEMES_KEY, JSON.stringify({}));
        }

        // 기존 광고제거권/VIP 데이터를 새로운 구독 형식으로 변환
        if (data.adFree || data.vip) {
          let subscriptionData = null;

          // VIP가 있으면 30일 프리미엄으로 처리
          if (data.vip && data.vip.active && new Date(data.vip.expiresAt) > new Date()) {
            subscriptionData = {
              type: '30d',
              purchasedAt: data.vip.purchasedAt,
              expiresAt: data.vip.expiresAt,
              paymentId: data.vip.paymentId,
              txid: data.vip.txid
            };
          }
          // VIP가 없으면 광고제거권 확인
          else if (data.adFree && new Date(data.adFree.expiresAt) > new Date()) {
            subscriptionData = {
              type: mapStoredAdFreeType(data.adFree.type),
              purchasedAt: data.adFree.purchasedAt,
              expiresAt: data.adFree.expiresAt,
              paymentId: data.adFree.paymentId,
              txid: data.adFree.txid
            };
          }

          setSubscriptionStatus(subscriptionData);
        } else {
          setSubscriptionStatus(null);
        }

        // 🔧 [정리] GPS 권한은 위 독립 useEffect에서 이미 로드하므로 중복 방지
        // 주석 처리하여 불필요한 DB 읽기 및 경쟁 조건 방지
        /*
        if (data.gpsPermission) {
          debugLog('[AppContext] GPS 권한 정보 수신:', data.gpsPermission);
          setSavedGpsPermission(data.gpsPermission);
        } else {
          setSavedGpsPermission(null);
        }
        */

        // [추가] 초기 동기화 성공
        setInitialSyncError(false);

      } catch (apiError) {
        errorLog('[AppContext] Firestore 직접 읽기 실패:', apiError);

        // [추가] 초기 동기화 실패 상태 설정
        setInitialSyncError(true);

        // [수정] API 실패 시 에러를 throw하여 상위에서 처리
        throw apiError;
      }
    } catch (error) {
      errorLog('[AppContext] 구독 상태 확인 실패:', error);
      // [추가] 초기 동기화 실패 상태 설정
      setInitialSyncError(true);
      throw error;
    } finally {
      isCheckingSubscriptionRef.current = false;
    }

    debugLog('[AppContext] ===== checkSubscriptionStatus 종료 =====');
  }, [isGuest, environment, checkSubscriptionStatusFromFirestore, storageKeys, piUser]);

  // 🎯 [근본적 해결] 게임 통계 전용 동기화 함수
  const syncGameStats = useCallback(async () => {
    const userId = sessionStorage.getItem('pi-user-id');

    if (!userId || isGuest()) {
      debugLog('[AppContext] 게임 통계 동기화 스킵 - 게스트 또는 사용자 없음');
      return;
    }

    try {
      debugLog('[AppContext] 🎯 게임 통계 동기화 시작');

      // 로컬 데이터 가져오기
      const localScore = parseInt(localStorage.getItem(storageKeys.HONOR_SCORE_KEY) || '0');
      const localClearCount = parseInt(localStorage.getItem(storageKeys.CLEAR_COUNT_KEY) || '0');
      const localPpPoints = parseInt(localStorage.getItem(`painori-pp-points-${environment}`) || '0');

      debugLog('[AppContext] 로컬 데이터:', { localScore, localClearCount, localPpPoints });

      // 서버 데이터 가져오기
      const userCollectionPath = getCollectionPath('users', environment);
      const userDocRef = doc(db, userCollectionPath, userId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        debugLog('[AppContext] 사용자 문서 없음 - 로컬 데이터만 사용');
        return;
      }

      const serverData = userDocSnap.data();
      const serverScore = serverData.totalScore || 0;
      const serverClearCount = serverData.totalClearCount || 0;
      const serverPpPoints = serverData.ppPoints || 0;

      debugLog('[AppContext] 서버 데이터:', { serverScore, serverClearCount, serverPpPoints });

      // 🎯 [충돌 해결] 로컬 우선 정책 적용 + 서버 데이터 삭제 감지
      const shouldUseLocal = (localValue, serverValue, dataType) => {
        // 🔧 [서버 데이터 삭제 감지] 서버 데이터가 0이고 로컬 데이터가 0보다 크면 서버 우선
        if (serverValue === 0 && localValue > 0) {
          debugLog(`[AppContext] ${dataType} 서버 데이터 삭제 감지 - 서버 우선:`, localValue, '->', serverValue);
          return false; // 서버 우선 (로컬 데이터도 0으로 리셋)
        }

        // 로컬 데이터가 더 크면 로컬 우선 (사용자가 더 많이 진행)
        if (localValue > serverValue) {
          debugLog(`[AppContext] ${dataType} 로컬 우선:`, localValue, '>', serverValue);
          return true;
        }
        // 서버 데이터가 더 크면 서버 우선 (다른 기기에서 진행)
        else if (serverValue > localValue) {
          debugLog(`[AppContext] ${dataType} 서버 우선:`, serverValue, '>', localValue);
          return false;
        }
        // 같으면 로컬 유지
        return true;
      };

      // 점수 충돌 해결
      if (shouldUseLocal(localScore, serverScore, '점수')) {
        debugLog('[AppContext] 점수 로컬 유지:', localScore);
      } else {
        debugLog('[AppContext] 점수 서버 동기화:', localScore, '->', serverScore);
        setTotalHonorScore(serverScore);
        localStorage.setItem(storageKeys.HONOR_SCORE_KEY, serverScore.toString());

        // 🔧 [서버 데이터 삭제 감지] 서버 데이터가 0이면 로컬 데이터도 0으로 리셋
        if (serverScore === 0 && localScore > 0) {
          debugLog('[AppContext] 서버 데이터 삭제 감지 - 로컬 점수 리셋:', localScore, '->', 0);
        }
      }

      // 클리어 횟수 충돌 해결
      if (shouldUseLocal(localClearCount, serverClearCount, '클리어 횟수')) {
        debugLog('[AppContext] 클리어 횟수 로컬 유지:', localClearCount);
      } else {
        debugLog('[AppContext] 클리어 횟수 서버 동기화:', localClearCount, '->', serverClearCount);
        setTotalClearCount(serverClearCount);
        localStorage.setItem(storageKeys.CLEAR_COUNT_KEY, serverClearCount.toString());

        // 🔧 [서버 데이터 삭제 감지] 서버 데이터가 0이면 로컬 데이터도 0으로 리셋
        if (serverClearCount === 0 && localClearCount > 0) {
          debugLog('[AppContext] 서버 데이터 삭제 감지 - 로컬 클리어 횟수 리셋:', localClearCount, '->', 0);
        }
      }

      // 🔧 [STAR 포인트 보안 강화] 서버 우선 정책 적용 (토큰 연계를 위한 보안)
      // STAR 포인트는 재화이므로 서버 데이터를 항상 우선으로 적용
      if (serverPpPoints !== localPpPoints) {
        // 🔧 [STAR 포인트 동기화 수정] 모든 불일치 시 서버 우선 동기화
        debugLog('[AppContext] STAR 포인트 서버 우선 동기화:', localPpPoints, '->', serverPpPoints);
        setPpPoints(serverPpPoints);
        localStorage.setItem(`painori-pp-points-${environment}`, serverPpPoints.toString());

        // 🔧 [UI 동기화 강화] STAR 포인트 동기화 완료 이벤트 발생
        window.dispatchEvent(new CustomEvent('starPointsSynced', {
          detail: {
            oldValue: localPpPoints,
            newValue: serverPpPoints,
            source: 'server_sync'
          }
        }));

        // 🔧 [보안 로그] STAR 포인트 불일치 감지
        if (Math.abs(serverPpPoints - localPpPoints) > 0) {
          debugLog('[AppContext] ⚠️ STAR 포인트 불일치 감지:', {
            local: localPpPoints,
            server: serverPpPoints,
            difference: Math.abs(serverPpPoints - localPpPoints)
          });
        }
      } else {
        debugLog('[AppContext] STAR 포인트 일치:', serverPpPoints);
      }

      // 🎯 [티켓 DB 동기화] 서버 우선 정책 적용
      const serverTicketCount = serverData ? (serverData.ticketCount || 0) : 0;
      const localTicketCount = ticketCount;
      if (serverTicketCount !== localTicketCount) {
        debugLog('[AppContext] 티켓 서버 우선 동기화:', localTicketCount, '->', serverTicketCount);
        setTicketCount(serverTicketCount);
        localStorage.setItem(`painori-ticket-count-${environment}`, serverTicketCount.toString());
      } else {
        debugLog('[AppContext] 티켓 일치:', serverTicketCount);
      }

      // 🔧 [점수 갱신 문제 해결] 점수도 서버 우선 정책 적용
      if (serverScore !== localScore) {
        debugLog('[AppContext] 점수 서버 우선 동기화:', localScore, '->', serverScore);
        setTotalHonorScore(serverScore);
        localStorage.setItem(storageKeys.HONOR_SCORE_KEY, serverScore.toString());
      } else {
        debugLog('[AppContext] 점수 일치:', serverScore);
      }

      debugLog('[AppContext] 🎯 게임 통계 동기화 완료');

    } catch (error) {
      errorLog('[AppContext] 게임 통계 동기화 실패:', error);
      // 실패해도 로컬 데이터는 유지
    }
  }, [environment, isGuest, storageKeys.HONOR_SCORE_KEY, storageKeys.CLEAR_COUNT_KEY, getCollectionPath, ticketCount]);

  // [🎯 최적화] 사용자 로그인 시 구독 상태 확인 - 의존성 최적화 및 중복 호출 방지
  useEffect(() => {
    // [🎯 최적화] userId를 useEffect 내부에서 가져와서 의존성 제거
    const userId = sessionStorage.getItem('pi-user-id');
    debugLog('[AppContext] 로그인 체크 - 사용자 존재:', !!userId);

    // [🎯 비용 최적화] 게스트 모드에서는 구독 상태 확인 완전 스킵
    if (userId && !isGuest()) {
      // [🎯 최적화] 약간의 지연으로 불필요한 초기 호출 방지
      const timer = setTimeout(() => {
        debugLog('[AppContext] 사용자 로그인 감지 - checkSubscriptionStatus 호출');
        checkSubscriptionStatus();
        syncGameStats(); // 🎯 [UI Fix] 게임 통계 동기화 추가
      }, 500);

      return () => clearTimeout(timer);
    } else if (isGuest()) {
      debugLog('[AppContext] 게스트 모드 - 구독 상태 확인 스킵 (비용 절감)');
    }
  }, [isGuest, checkSubscriptionStatus, syncGameStats]); // 🔧 수정: 의존성 추가

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setCurrentLanguage(lng);
  };

  const toggleSound = () => {
    setIsSoundEnabled(prev => !prev);
    // [수정] BGM도 함께 토글 - 올바른 메서드명 사용
    bgmManager.toggleSound();
  };

  // 🎯 [추가] 진동 토글 함수
  const toggleVibration = () => {
    setIsVibrationEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('painori-vibration-enabled', newValue.toString());
      return newValue;
    });
  };

  // 🎯 [추가] 진동 트리거 함수
  const triggerVibration = (pattern = 100) => {
    if (isVibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  // 🎯 [Cost] STAR 포인트는 위의 user 문서 onSnapshot(380행)에서 이미 동기화됨. 중복 구독 제거.

  // [추가] 게임 상태 저장 함수
  const saveGameState = useCallback(async (gameData) => {
    try {
      if (!piUser?.uid) return;

      const gameState = {
        currentStage: gameData.currentStage || 1,
        score: gameData.score || 0,
        level: gameData.level || 1,
        timestamp: new Date().toISOString(),
        environment: propEnvironment
      };

      // 로컬 스토리지에 임시 저장
      localStorage.setItem('spotnori-game-state', JSON.stringify(gameState));

      debugLog('[GameState] 게임 상태 저장 완료:', maskSensitiveData(gameState));
    } catch (error) {
      errorLog('[GameState] 게임 상태 저장 실패:', error.message);
    }
  }, [piUser?.uid, propEnvironment]);

  // [추가] 게임 상태 복원 함수
  const restoreGameState = useCallback(() => {
    try {
      const savedState = localStorage.getItem('spotnori-game-state');
      if (savedState) {
        const gameState = JSON.parse(savedState);
        debugLog('[GameState] 게임 상태 복원:', maskSensitiveData(gameState));
        return gameState;
      }
      return null;
    } catch (error) {
      errorLog('[GameState] 게임 상태 복원 실패:', error.message);
      return null;
    }
  }, []);

  // [추가] 게임 상태 삭제 함수
  const clearGameState = useCallback(() => {
    try {
      localStorage.removeItem('spotnori-game-state');
      debugLog('[GameState] 게임 상태 삭제 완료');
    } catch (error) {
      errorLog('[GameState] 게임 상태 삭제 실패:', error.message);
    }
  }, []);

  const playSound = (soundName) => {
    if (!isSoundEnabled) return;
    debugLog(`Sound would play: ${soundName}`);
  };

  // [🔧 수정] Firebase 업데이트를 위한 디바운스 함수 - 완전히 비활성화


  // [🔧 수정] 점수 업데이트 시 즉시 UI 반영 + Firebase 동기화 + UI 이벤트 발생
  const addHonorScore = useCallback((score) => {
    setTotalHonorScore(prev => {
      const newScore = prev + score;
      debugLog('[AppContext] 명예 점수 추가 (즉시 UI 업데이트):', score, '총점:', newScore);

      // [🔧 추가] localStorage 즉시 업데이트로 동기화 보장
      localStorage.setItem(storageKeys.HONOR_SCORE_KEY, newScore.toString());
      debugLog('[AppContext] localStorage 즉시 동기화 완료:', newScore);

      // 🎯 [단순화] 이벤트 발생 제거 - React 상태 변경으로 자동 리렌더링

      return newScore;
    });
  }, [storageKeys.HONOR_SCORE_KEY]);

  const getCurrentTier = () => {
    return getTierByScore(totalHonorScore);
  };

  // [🎯 최적화] 테마별 최고 점수 업데이트 함수 - useCallback으로 메모이제이션
  const updateThemeHighScore = useCallback((themeId, score) => {
    setThemeHighScores(prev => {
      const currentBest = prev[themeId] || 0;
      if (score > currentBest) {
        debugLog(`[AppContext] 테마 ${themeId} 최고 점수 갱신: ${currentBest} → ${score}`);
        const newScores = {
          ...prev,
          [themeId]: score
        };
        // localStorage에도 즉시 저장
        localStorage.setItem(storageKeys.THEME_HIGH_SCORES_KEY, JSON.stringify(newScores));
        return newScores;
      }
      return prev;
    });
  }, [storageKeys.THEME_HIGH_SCORES_KEY]);

  // [신규] 특정 테마의 최고 점수 가져오기
  const getThemeHighScore = useCallback((themeId) => {
    return themeHighScores[themeId] || 0;
  }, [themeHighScores]);

  // [통합] 구독 활성 상태 확인 - 성능 최적화
  const hasActiveSubscription = useCallback(() => {
    // 🎯 [성능 최적화] 짧은 시간 내 중복 호출 방지 (1초 캐시)
    const now = Date.now();
    if (hasActiveSubscriptionCache.current.result !== null &&
      (now - hasActiveSubscriptionCache.current.timestamp) < 1000) {
      return hasActiveSubscriptionCache.current.result;
    }

    // 🎯 [수정] 서버 캐시 우선 확인 (실시간 반영)
    const serverData = subscriptionCache.current.data;
    if (serverData) {
      // VIP가 있으면 30일 프리미엄으로 처리
      if (serverData.vip && serverData.vip.active && new Date(serverData.vip.expiresAt) > new Date()) {
        debugLog('[AppContext] 구독 상태 확인 (서버 VIP): true');
        hasActiveSubscriptionCache.current = { result: true, timestamp: now };
        return true;
      }
      // VIP가 없으면 광고제거권 확인
      if (serverData.adFree && new Date(serverData.adFree.expiresAt) > new Date()) {
        debugLog('[AppContext] 구독 상태 확인 (서버 AdFree): true');
        hasActiveSubscriptionCache.current = { result: true, timestamp: now };
        return true;
      }
    }

    // 🎯 [백업] 로컬 상태 확인
    if (!subscriptionStatus) {
      debugLog('[AppContext] 구독 상태 확인 (로컬): false - subscriptionStatus 없음');
      hasActiveSubscriptionCache.current = { result: false, timestamp: now };
      return false;
    }

    const isActive = new Date(subscriptionStatus.expiresAt) > new Date();
    debugLog('[AppContext] 구독 상태 확인 (로컬):', isActive);
    hasActiveSubscriptionCache.current = { result: isActive, timestamp: now };
    return isActive;
  }, [subscriptionStatus]);

  // [통합] 광고 제거 효과 확인 - 모든 구독 타입에서 광고 제거
  const shouldRemoveAds = useCallback(() => {
    return hasActiveSubscription();
  }, [hasActiveSubscription]);

  // [추가] 구독 타입별 혜택 확인
  const getActiveSubscriptionBenefits = useCallback(() => {
    if (!hasActiveSubscription()) return null;
    return getSubscriptionBenefits(subscriptionStatus);
  }, [hasActiveSubscription, subscriptionStatus]);

  // [하위 호환성] 기존 함수들 - deprecated이지만 유지
  const hasActiveAdFree = useCallback(() => {
    warnLog('[AppContext] hasActiveAdFree is deprecated. Use hasActiveSubscription instead.');
    return hasActiveSubscription() && ['1d', '7d'].includes(subscriptionStatus?.type);
  }, [hasActiveSubscription, subscriptionStatus]);

  const hasActiveVip = useCallback(() => {
    warnLog('[AppContext] hasActiveVip is deprecated. Use hasActiveSubscription instead.');
    return hasActiveSubscription() && subscriptionStatus?.type === '30d';
  }, [hasActiveSubscription, subscriptionStatus]);

  // [🔧 수정] 클리어 횟수 증가 함수 - localStorage 저장 추가 + UI 이벤트 발생
  const addClearCount = useCallback(() => {
    setTotalClearCount(prev => {
      const newCount = prev + 1;
      localStorage.setItem(storageKeys.CLEAR_COUNT_KEY, newCount.toString());
      debugLog('[AppContext] 클리어 횟수 증가 (UI + localStorage 업데이트):', newCount);

      // 🎯 [단순화] 이벤트 발생 제거 - React 상태 변경으로 자동 리렌더링

      return newCount;
    });
  }, [storageKeys.CLEAR_COUNT_KEY]);



  // 🎯 [Safety] 마지막 상태 업데이트 시간 (중복 처리 방지)
  const lastMetricsUpdateRef = useRef(0);

  // 🎯 [Batching] PP 포인트 배치 처리를 위한 큐 및 상태 관리
  const ppQueueRef = useRef([]);
  const PP_QUEUE_KEY = `painori-pp-queue-${environment}`;
  const isFlushingRef = useRef(false);

  // 🎯 [Stabilization] 큐 복구 및 24시간 만료 체크
  useEffect(() => {
    try {
      const savedQueue = localStorage.getItem(PP_QUEUE_KEY);
      if (savedQueue) {
        const parsed = JSON.parse(savedQueue);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const now = Date.now();
          const ONE_DAY = 24 * 60 * 60 * 1000;

          const validItems = parsed.filter(item => {
            if (!item.timestamp || !item.id) return false;
            const age = now - item.timestamp;
            // 24시간 지난 요청 만료 처리
            if (age > ONE_DAY) {
              warnLog(`[AppContext] 만료된 PP 요청 폐기: ${item.id} (${(age / 3600000).toFixed(1)}h old)`);
              return false;
            }
            return true;
          });

          if (validItems.length > 0) {
            debugLog(`[AppContext] ♻️ Restored ${validItems.length} pending PP requests from storage`);
            ppQueueRef.current = validItems;
            flushPpQueue(); // 즉시 처리 시도
          } else {
            localStorage.removeItem(PP_QUEUE_KEY);
          }
        }
      }
    } catch (e) {
      console.warn('[AppContext] Failed to restore PP queue:', e);
      localStorage.removeItem(PP_QUEUE_KEY);
    }
  }, [environment]);

  const saveQueue = () => {
    try {
      if (ppQueueRef.current.length > 0) {
        localStorage.setItem(PP_QUEUE_KEY, JSON.stringify(ppQueueRef.current));
      } else {
        localStorage.removeItem(PP_QUEUE_KEY);
      }
    } catch (e) { /* ignore */ }
  };

  /**
   * 🎯 [Batching] PP 포인트 추가 (Client-Side Queueing)
   */
  const addPpPoints = useCallback((amount, source, ticketDelta = 0) => {
    // 🎯 [Validation] 입력값 검증
    if (typeof amount !== 'number' || (amount === 0 && ticketDelta === 0)) return;

    // 비정상적인 큰 값이나 음수는 배치에서 제외하고 별도 에러 처리 (또는 무시)
    // 단, rewardUser는 양수만 취급. 소비는 spendPpPoints 사용.
    if (amount < 0 && source !== 'debug') {
      warnLog('[AppContext] addPpPoints received negative amount. Use spendPpPoints instead.', { amount, source });
      return;
    }

    // 1. Optimistic Update
    if (amount !== 0) setPpPoints(prev => prev + amount);
    if (ticketDelta !== 0) setTicketCount(prev => prev + ticketDelta);

    // 2. Add to Queue
    const requestItem = {
      id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      amount,
      source,
      ticketDelta,
      timestamp: Date.now()
    };

    // 🎯 [Safety] MAX_QUEUE_SIZE Check
    if (ppQueueRef.current.length >= 1000) {
      warnLog('[AppContext] ⚠️ PP Queue Overflow (Over 1000 items) - Forcing Flush');
      flushPpQueue();
      // Flush 후에도 꽉 찼으면 가장 오래된 것 제거 (메모리 보호)
      if (ppQueueRef.current.length >= 1000) {
        ppQueueRef.current.shift();
      }
    }

    ppQueueRef.current.push(requestItem);
    saveQueue();

    debugLog(`[AppContext] ➕ PP buffered: ${amount} (Queue: ${ppQueueRef.current.length})`);

    // 3. Trigger Flush if threshold met
    if (ppQueueRef.current.length >= 10) {
      flushPpQueue();
    }
  }, [environment]);

  /**
   * 🎯 [Batching] 큐 플러시
   */
  const flushPpQueue = async () => {
    if (isFlushingRef.current || ppQueueRef.current.length === 0) return;

    // Pi User 없으면 대기 (로그인 후 처리됨)
    const storedUid = sessionStorage.getItem('pi-user-id') || piUser?.uid;
    if (!storedUid) return;

    isFlushingRef.current = true;

    const queueSnapshot = [...ppQueueRef.current];
    const totalAmount = queueSnapshot.reduce((sum, item) => sum + (item.amount || 0), 0);
    const totalTicketDelta = queueSnapshot.reduce((sum, item) => sum + (item.ticketDelta || 0), 0); // 🎯 티켓 집계
    const requestIds = queueSnapshot.map(item => item.id);

    // Source Map
    const sourceMap = {};
    queueSnapshot.forEach(item => {
      sourceMap[item.source] = (sourceMap[item.source] || 0) + (item.amount || 0);
    });
    const mainSource = Object.keys(sourceMap).reduce((a, b) => sourceMap[a] > sourceMap[b] ? a : b, 'batch_earning');

    debugLog(`[AppContext] 🚀 Flushing PP Queue: ${queueSnapshot.length} items, Total: ${totalAmount}`);

    try {
      const result = await updatePpPointsBatch(
        storedUid,
        requestIds,
        totalAmount,
        mainSource,
        environment,
        sourceMap,
        totalTicketDelta
      );

      // 전송 성공 시 해당 ID들만 큐에서 제거
      ppQueueRef.current = ppQueueRef.current.filter(item => !requestIds.includes(item.id));
      saveQueue();
      debugLog('[AppContext] ✅ PP Batch Sync Success');

      // 서버 값으로 보정
      if (result.ppPoints !== undefined) setPpPoints(result.ppPoints);
      if (result.ticketCount !== undefined) setTicketCount(result.ticketCount);

    } catch (error) {
      errorLog('[AppContext] ❌ PP Batch Sync Failed (Will retry):', error);
      // 실패 시 큐 유지 (다음 주기 재시도)
    } finally {
      isFlushingRef.current = false;
    }
  };

  // 🎯 [Batching] 일반 적립은 20초 단위로 묶고, 화면 전환/숨김/10건 도달 시에는 즉시 전송
  useEffect(() => {
    const timer = setInterval(() => {
      if (ppQueueRef.current.length > 0) {
        flushPpQueue();
      }
    }, 20000);
    return () => clearInterval(timer);
  }, [environment]);

  // 🎯 [Safety] 앱 종료/백그라운드 전환 시 자동 저장/전송 (Best Effort)
  useEffect(() => {
    const handleUnload = () => { saveQueue(); };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (ppQueueRef.current.length > 0) {
          debugLog('[AppContext] 🙈 App hidden - Flushing PP Queue immediately');
          flushPpQueue();
        }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 🔧 [Legacy Support] 기존 이벤트 핸들러 유지하되 새 로직 연결
  const setupImmediateFlushEvents = useCallback(() => {
    const triggerFlush = () => flushPpQueue();

    window.addEventListener('adEnd', triggerFlush);
    window.addEventListener('stageClear', triggerFlush);
    window.addEventListener('navigateToShop', triggerFlush);
    window.addEventListener('navigateToRanking', triggerFlush);
    window.addEventListener('online', triggerFlush);

    return () => {
      window.removeEventListener('adEnd', triggerFlush);
      window.removeEventListener('stageClear', triggerFlush);
      window.removeEventListener('navigateToShop', triggerFlush);
      window.removeEventListener('navigateToRanking', triggerFlush);
      window.removeEventListener('online', triggerFlush);
    };
  }, []);

  // [추가] STAR 포인트 복구 함수 - 다른 키에서 데이터 찾기
  const recoverPpPoints = useCallback(() => {
    // 모든 pp-points 관련 키 확인
    const allKeys = Object.keys(localStorage).filter(k => k.includes('pp-points'));
    debugLog('[AppContext] STAR 포인트 복구 시도:', { allKeys, currentEnvironment: environment });

    for (const key of allKeys) {
      const saved = localStorage.getItem(key);
      if (saved && parseInt(saved) > 0) {
        const points = parseInt(saved);
        setPpPoints(points);
        // 현재 환경 키로도 저장
        localStorage.setItem(`painori-pp-points-${environment}`, points.toString());
        debugLog('[AppContext] STAR 포인트 복구 성공:', { key, points, environment });
        return true;
      }
    }

    debugLog('[AppContext] STAR 포인트 복구할 데이터 없음');
    return false;
  }, [environment]);

  // 🔧 [V1.5] 동기화 이벤트 기반 호출 - 폴링 금지
  const syncPpPoints = useCallback(async (force = false) => {
    const now = Date.now();

    // 강제 동기화가 아니면 조건 확인
    if (!force && (now - (window.lastPpSyncTime || 0)) < 600000) { // 10분
      debugLog('[AppContext] 동기화 스킵 - 최소 간격 미충족');
      return;
    }

    try {
      const userId = sessionStorage.getItem('pi-user-id');
      if (!userId || isGuest()) return; // 🔧 [Fix] 게스트 체크 추가

      debugLog('[AppContext] STAR 포인트 동기화 시작:', { force, userId });

      // 서버에서 최신 데이터 가져오기
      const result = await updatePpPoints(userId, 0, 'sync_check', environment, `sync_${Date.now()}`);

      if (result && result.ppPoints !== undefined) {
        // UI 업데이트
        setPpPoints(result.ppPoints);
        localStorage.setItem(`painori-pp-points-${environment}`, result.ppPoints.toString());
        window.lastPpSyncTime = now;

        debugLog('[AppContext] STAR 포인트 동기화 완료:', { ppPoints: result.ppPoints });
      }

    } catch (error) {
      debugLog('[AppContext] 동기화 실패 (무시):', error);
    }
  }, [environment, isGuest]);

  // 🔧 [V1.5] 사용자 친화적 에러 메시지 생성
  const getErrorMessage = useCallback((error) => {
    switch (error.message) {
      case 'Invalid amount':
        return '잘못된 금액입니다.';
      case 'Guest users cannot spend STAR points':
        return '게스트는 STAR 포인트를 사용할 수 없습니다.';
      case 'PURCHASE_IN_PROGRESS':
        return '구매 처리 중입니다. 잠시만 기다려주세요.';
      case 'Server validation failed':
        return '서버 검증에 실패했습니다. 다시 시도해주세요.';
      case 'Insufficient PP points':
        return 'STAR 포인트가 부족합니다.';
      case 'User not authenticated':
        return '로그인이 필요합니다.';
      default:
        return '구매 처리 중 오류가 발생했습니다. 다시 시도해주세요.';
    }
  }, []);

  // 🔧 [V1.5] 이벤트 기반 동기화 설정
  const setupEventBasedSync = useCallback(() => {
    const handleFocus = () => {
      const timeSinceLastSync = Date.now() - (window.lastPpSyncTime || 0);
      if (timeSinceLastSync > 7200000) { // 120분 이상 (간격 대폭 증가)
        debugLog('[AppContext] 앱 포커스 복귀 - 장시간 후 동기화 실행');
        syncPpPoints();
      } else {
        debugLog('[AppContext] 앱 포커스 복귀 - 동기화 스킵 (최근 동기화됨)');
      }

      // 포커스 복귀 시 배치 전송 (누락된 포인트 전송)
      if (ppQueueRef.current.length > 0) {
        debugLog('[AppContext] 앱 포커스 복귀 - 배치 전송 실행');
        flushPpQueue();
      }
    };
    const handlePurchaseSuccess = () => {
      debugLog('[AppContext] 구매 성공 - 서버 응답으로 이미 동기화됨');
      // 구매 성공 시에는 이미 서버 응답으로 동기화됨 - 추가 동기화 불필요
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const timeSinceLastSync = Date.now() - (window.lastPpSyncTime || 0);
        if (timeSinceLastSync > 7200000) { // 120분 이상 (간격 대폭 증가)
          debugLog('[AppContext] 장시간 백그라운드 후 복귀 - 동기화 실행');
          syncPpPoints();
        } else {
          debugLog('[AppContext] 백그라운드 복귀 - 동기화 스킵 (최근 동기화됨)');
        }
      }
    };
    const handleOnline = () => {
      const timeSinceLastSync = Date.now() - (window.lastPpSyncTime || 0);
      if (timeSinceLastSync > 7200000) { // 120분 이상 (간격 대폭 증가)
        debugLog('[AppContext] 온라인 복귀 - 장시간 후 동기화 실행');
        syncPpPoints();
      } else {
        debugLog('[AppContext] 온라인 복귀 - 동기화 스킵 (최근 동기화됨)');
      }
    };

    // 앱 포커스 복귀
    window.addEventListener('focus', handleFocus);

    // 구매/소비 성공 직후
    window.addEventListener('purchaseSuccess', handlePurchaseSuccess);

    // 장시간 백그라운드 후 첫 진입
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 온라인 복귀
    window.addEventListener('online', handleOnline);

    // 정리 함수 반환
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('purchaseSuccess', handlePurchaseSuccess);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPpPoints]);

  // 🔧 [V2.0] 지출 이벤트 기반 동기화 - 티켓 동기화 지원
  const spendPpPoints = useCallback(async (amount, item = 'unknown', ticketDelta = null) => {
    const userId = sessionStorage.getItem('pi-user-id');

    // 🔧 [보안 강화] 입력값 검증
    if (typeof amount !== 'number' || amount <= 0 || amount > 10000) {
      const error = new Error('Invalid amount');
      errorLog('[AppContext] STAR 포인트 소비 실패 - 잘못된 값:', { amount, item });
      throw error;
    }

    // 🔧 [보안 강화] 게스트는 STAR 포인트 소비 불가
    if (isGuest()) {
      const error = new Error('Guest users cannot spend STAR points');
      debugLog('[AppContext] 게스트는 STAR 포인트 소비 불가');
      throw error;
    }

    // 🔧 [V1.5] 중복 처리 방지
    if (window.isPurchasing) {
      const error = new Error('PURCHASE_IN_PROGRESS');
      errorLog('[AppContext] 구매 처리 중 - 중복 요청 차단:', { amount, item });
      throw error;
    }

    // 🔧 [V1.5] 지출 처리 - 폴링 금지, 이벤트 기반 동기화
    if (userId) {
      window.isPurchasing = true;

      try {
        // 1. 로딩 표시 (UI는 건드리지 않음)
        debugLog('[AppContext] STAR 포인트 지출 시작 - 서버 검증 대기:', { amount, item });

        // 2. 서버 검증 먼저 (폴링 금지) - 🎯 [V2.0] 티켓 증가량도 함께 전달
        const requestId = `${userId}_${Date.now()}_${Math.random()}`;
        const result = await updatePpPoints(userId, -amount, `spend_${item}`, environment, requestId, ticketDelta);

        // 3. 서버 응답 받으면 그때만 갱신
        if (result && result.ppPoints !== undefined) {
          setPpPoints(result.ppPoints);
          localStorage.setItem(`painori-pp-points-${environment}`, result.ppPoints.toString());
          debugLog('[AppContext] STAR 포인트 지출 성공 - 서버 응답 반영:', { amount, item, newTotal: result.ppPoints });

          // 4. 서버 응답으로 충분한 동기화 - setTimeout 제거로 호출 감소

          // 5. 구매 성공 이벤트 발생
          window.dispatchEvent(new CustomEvent('purchaseSuccess', {
            detail: { amount, item, ppPoints: result.ppPoints }
          }));

          return true;
        } else {
          const error = new Error('Server validation failed');
          errorLog('[AppContext] STAR 포인트 서버 검증 실패:', { amount, item });
          throw error;
        }
      } catch (error) {
        errorLog('[AppContext] STAR 포인트 지출 오류:', error);
        // 사용자 친화적 에러 메시지로 변환
        const userFriendlyError = new Error(getErrorMessage(error));
        userFriendlyError.originalError = error;
        throw userFriendlyError;
      } finally {
        window.isPurchasing = false;
      }
    }

    const error = new Error('User not authenticated');
    throw error;
  }, [environment, isGuest, getErrorMessage]);

  // [수정] 그림팩 캐시 삭제 함수 - 모든 테마 초기화
  const clearImageCache = async () => {
    debugLog('[AppContext] 그림팩 캐시 삭제');
    await imageLoader.clearAllCache();
    // 다운로드 상태는 ThemeDownloadContext에서 관리하므로 제거
  };

  // [추가] 개발자 리셋 함수들
  const resetPurchasedThemes = () => {
    debugLog('[AppContext] 구매 테마 리셋');
    setPurchasedThemes({});
    localStorage.removeItem(storageKeys.PURCHASED_THEMES_KEY);
    // 영수증도 삭제
    const receiptKey = `painori-receipts-${environment}`;
    localStorage.removeItem(receiptKey);
  };

  const resetHonorScore = () => {
    debugLog('[AppContext] 명예 점수 리셋');
    setTotalHonorScore(0);
    localStorage.removeItem(storageKeys.HONOR_SCORE_KEY);
  };

  const clearThemeProgress = () => {
    debugLog('[AppContext] 테마 진행도 리셋');
    // 테마별 진행도 삭제
    localStorage.removeItem('painori-theme-progress');

    // 테마별 최고 점수 삭제 - [수정] 새로운 방식으로 변경
    setThemeHighScores({});
    localStorage.removeItem(storageKeys.THEME_HIGH_SCORES_KEY);

    // 기존 방식의 최고 점수도 삭제 (하위 호환성)
    const themes = ['masterpiece_1', 'masterpiece_2', 'masterpiece_3', 'masterpiece_4', 'masterpiece_5', 'masterpiece_6'];
    themes.forEach(themeId => {
      localStorage.removeItem(`painori-best-score-${themeId}`);
    });

    // [수정] 클리어 횟수도 리셋
    setTotalClearCount(0);
    localStorage.removeItem(storageKeys.CLEAR_COUNT_KEY);

    // [수정] 다운로드 상태는 ThemeDownloadContext에서 관리하므로 제거
  };

  // [🔧 수정] Firebase 게임 통계 리셋 - updateUserStats 사용 중단
  const resetGameStats = async () => {
    debugLog('[AppContext] 게임 통계 리셋');
    const userId = sessionStorage.getItem('pi-user-id');

    // 로컬 리셋
    resetHonorScore();
    clearThemeProgress();

    // Firebase 리셋 임시 비활성화 - 게스트는 스킵
    if (userId && !isGuest()) {
      debugLog('[AppContext] Firebase 게임 통계 리셋 스킵 - saveGameResult 통합 처리로 변경');
    }
  };

  const resetAllGameData = async () => {
    debugLog('[AppContext] 전체 게임 데이터 리셋');
    resetPurchasedThemes();
    await resetGameStats(); // Firebase 포함 리셋

    // [추가] 그림팩 캐시도 삭제
    await clearImageCache();

    // 게임 플레이 관련 상태는 GamePlayContext에서 관리하므로 제거
  };

  // 구독 상태 리셋 - 시스템 도구에서 접근 가능
  const resetSubscriptionStatus = async () => {
    debugLog('[AppContext] 구독 상태 리셋 - 시스템 도구 접근');
    setSubscriptionStatus(null);
    const subscriptionKey = `painori-subscription-${environment}`;
    localStorage.removeItem(subscriptionKey);

    // 기존 키들도 제거 (마이그레이션을 위해)
    const adFreeKey = `painori-adfree-${environment}`;
    const vipKey = `painori-vip-${environment}`;
    localStorage.removeItem(adFreeKey);
    localStorage.removeItem(vipKey);

    // Firebase에서도 제거 - 시스템 도구를 통한 접근
    const userId = sessionStorage.getItem('pi-user-id');
    if (userId && environment === 'testnet' && !isGuest()) {
      try {
        debugLog('[AppContext] Firebase 구독 상태 제거 시작');

        // 🎯 [Stage 3 수정] environment 파라미터 추가
        // 시스템 도구를 통한 제거 작업
        await Promise.all([
          resetAdFreeAPI(userId, '', environment).catch(e => errorLog('[AppContext] 광고제거 리셋 실패:', e)),
          resetVipAPI(userId, '', environment).catch(e => errorLog('[AppContext] VIP 리셋 실패:', e))
        ]);

        debugLog('[AppContext] Firebase에서 구독 상태 제거 완료');
      } catch (error) {
        errorLog('[AppContext] Firebase 구독 상태 제거 실패:', error);
      }
    }
  };

  // [하위 호환성] 기존 리셋 함수들 유지
  const resetAdFreeStatus = async () => {
    warnLog('[AppContext] resetAdFreeStatus is deprecated. Use resetSubscriptionStatus instead.');
    return resetSubscriptionStatus();
  };

  const resetVipStatus = async () => {
    warnLog('[AppContext] resetVipStatus is deprecated. Use resetSubscriptionStatus instead.');
    return resetSubscriptionStatus();
  };

  /* 
   * 🎯 [Refactored] Generic Pi Payment Processor
   * Supports both Legacy (Theme Object) and New (Generic) signatures.
   * Returns a Promise that resolves with { paymentId, txid } on success.
   */
  const processPiPayment = async (arg1, arg2, arg3) => {
    debugLog('[Payment] ===== Payment Process Started =====');

    return new Promise(async (resolve, reject) => {
      // [1] Argument Parsing
      let amount, memo, metadata, onSuccess, onError, isLegacyThemeMode = false;
      let theme = null;

      if (typeof arg1 === 'object' && arg1.id) {
        // [Legacy Mode] (theme, onSuccess, onError)
        debugLog('[Payment] Mode: Legacy Theme Purchase');
        isLegacyThemeMode = true;
        theme = arg1;
        amount = theme.price;
        memo = `${theme.displayName} 테마 구매`;
        metadata = {
          type: 'theme_purchase',
          themeId: theme.id,
          themeName: theme.displayName,
          // Environment & UserID will be added below
        };
        onSuccess = arg2;
        onError = arg3;
      } else {
        // [New Generic Mode] (amount, memo, metadata)
        debugLog('[Payment] Mode: Generic Purchase');
        amount = arg1;
        memo = arg2;
        metadata = arg3 || {};
      }

      const userId = sessionStorage.getItem('pi-user-id') || 'unknown';
      const finalMetadata = {
        ...metadata,
        userId: userId,
        timestamp: Date.now(),
        appVersion: '1.0.0',
        environment: environment
      };

      // [2] User & Environment Validation
      if (isGuest()) {
        const err = 'guest_payment_not_allowed';
        errorLog('[Payment] Guest payment restricted');
        if (onError) onError(err);
        return reject(new Error(err));
      }

      // 🎯 [Refactor] Use PiAdapter
      if (!PiAdapter.isReady()) {
        const err = 'Pi SDK not loaded';
        errorLog('[Payment] Pi SDK missing');
        if (onError) onError(err);
        return reject(new Error(err));
      }

      setIsProcessingPayment(true);

      try {
        debugLog('[Payment] Calling createPayment', { amount, memo });

        // 🎯 [Refactor] Use PiAdapter
        PiAdapter.createPayment({
          amount: amount,
          memo: memo,
          metadata: finalMetadata
        }, {
          onReadyForServerApproval: async function (paymentId) {
            debugLog('[Payment] onReadyForServerApproval', paymentId);
            try {
              const targetId = isLegacyThemeMode
                ? theme.id
                : (finalMetadata.type === 'star_purchase' ? finalMetadata.packageId : (finalMetadata.spotId || 'generic'));

              // 🎯 [수정] 매장 인증 결제 구분을 위해 paymentType 전달
              const paymentType = finalMetadata.type === 'verify'
                ? 'verify'
                : (finalMetadata.type === 'star_purchase' ? 'star_purchase' : (isLegacyThemeMode ? 'theme' : 'generic'));

              // Using approvePayment service
              await approvePayment(paymentId, userId, targetId, amount, environment, paymentType);
              debugLog('[Payment] Server Approval API Success');
            } catch (error) {
              errorLog('[Payment] Server Approval Failed', error);
              // 🎯 승인 실패 시에도 이후 복구/취소를 위해 paymentId를 예비 저장
              try {
                const fallback = {
                  identifier: paymentId,
                  source: 'server_approval_failed',
                  createdAt: Date.now()
                };
                sessionStorage.setItem('pi-incomplete-payment', JSON.stringify(fallback));
              } catch (e) {
                errorLog('[Payment] Failed to store fallback incomplete-payment (approval)', e);
              }
              // Do not reject here, let Pi SDK handle error flow or timeout
            }
          },
          onReadyForServerCompletion: async function (paymentId, txid) {
            debugLog('[Payment] onReadyForServerCompletion', { paymentId, txid });
            try {
              if (finalMetadata.type === 'ticket_purchase') {
                // 🎯 [Secure] Use purchaseTicket Cloud Function
                const purchaseTicketCallable = httpsCallable(functions, 'purchaseTicket');
                await purchaseTicketCallable({
                  paymentId,
                  txid,
                  ticketAmount: finalMetadata.ticketAmount,
                  environment
                });
                debugLog('[Payment] Ticket Purchase Function Success');
              } else if (finalMetadata.type === 'star_purchase') {
                // 🎯 [STAR 구매] 다른 결제와 동일한 패턴으로 처리
                // 1. 먼저 Pi Platform `/complete` 호출 (다른 결제와 동일)
                await completePayment(paymentId, txid, environment);
                debugLog('[Payment] STAR Payment Completion API Success');

                // 2. 그 다음 STAR 지급 (별도 처리로 타임아웃 방지)
                const result = await purchaseStarWithPi(
                  finalMetadata.packageId,
                  paymentId,
                  txid,
                  userId,
                  environment
                );
                debugLog('[Payment] STAR Purchase Function Success', result);

                // 🎯 [Fix] 서버 응답에서 ppPoints를 받아서 즉시 UI 업데이트
                // 🎯 [디버깅] 응답 구조 확인
                debugLog('[Payment] STAR 응답 구조 확인:', {
                  hasResult: !!result,
                  resultKeys: result ? Object.keys(result) : [],
                  ppPoints: result?.ppPoints,
                  success: result?.success,
                  star: result?.star
                });

                if (result && result.ppPoints !== undefined && typeof result.ppPoints === 'number') {
                  setPpPoints(result.ppPoints);
                  localStorage.setItem(`painori-pp-points-${environment}`, result.ppPoints.toString());
                  debugLog('[Payment] STAR UI 업데이트 완료', { ppPoints: result.ppPoints });

                  // 🎯 [Fix] STAR 구매 성공 이벤트 발생 (UI 즉시 반영)
                  window.dispatchEvent(new CustomEvent('purchaseSuccess', {
                    detail: {
                      type: 'star_purchase',
                      amount: finalMetadata.amount,
                      ppPoints: result.ppPoints
                    }
                  }));
                } else {
                  // Fallback: 동기화 호출
                  errorLog('[Payment] ⚠️ STAR 응답에 ppPoints 없음 또는 잘못된 형식 - 동기화 호출', {
                    result,
                    ppPointsType: result?.ppPoints ? typeof result.ppPoints : 'undefined'
                  });
                  await syncPpPoints(true);
                }
              } else {
                await completePayment(paymentId, txid, environment);
                debugLog('[Payment] Server Completion API Success');
              }

              // [Legacy Logic for Themes]
              if (isLegacyThemeMode) {
                const purchaseInfo = {
                  purchasedAt: new Date().toISOString(),
                  paymentId: paymentId,
                  txid: txid,
                  amount: amount,
                  environment: environment
                };

                // Update Local State
                setPurchasedThemes(prev => ({ ...prev, [theme.id]: purchaseInfo }));

                // Update Cache
                const currentServerData = subscriptionCache.current.data;
                if (currentServerData && currentServerData.purchasedThemes) {
                  subscriptionCache.current.data = {
                    ...currentServerData,
                    purchasedThemes: [...currentServerData.purchasedThemes, theme.id]
                  };
                }

                // Notify UI
                window.dispatchEvent(new CustomEvent('purchaseCompleted', {
                  detail: { type: 'theme', themeId: theme.id }
                }));

                // Save Receipt
                // Save Receipt
                // Needs to be careful if saveReceipt uses 'theme' object structure
              }

              setIsProcessingPayment(false);
              playSound('purchase_success'); // Assuming playSound available

              // Callback & Resolve
              if (onSuccess) onSuccess(isLegacyThemeMode ? theme.id : paymentId);
              resolve({ paymentId, txid });

            } catch (error) {
              // 🎯 [보안 규칙] /complete 실패 시에는 성공 처리 금지
              errorLog('[Payment] Server Completion API Failed', error);
              setIsProcessingPayment(false);

              // ✅ 이후 복구/재시도를 위해 sessionStorage에 저장
              try {
                const fallback = {
                  identifier: paymentId,
                  source: 'server_completion_failed',
                  createdAt: Date.now()
                };
                sessionStorage.setItem('pi-incomplete-payment', JSON.stringify(fallback));
              } catch (e) {
                errorLog('[Payment] Failed to store fallback incomplete-payment (completion)', e);
              }

              if (onError) onError('server_completion_failed');
              reject(error);
            }
          },
          onCancel: function (paymentId) {
            debugLog('[Payment] Cancelled', paymentId);
            setIsProcessingPayment(false);
            if (onError) onError('cancelled');
            reject(new Error('Payment cancelled by user'));
          },
          onError: async function (error, _payment) {
            errorLog('[Payment] Error', error);
            setIsProcessingPayment(false);

            // 🎯 [결제 복구] "출금됐는데 앱에서 완료 안 된" 경우 먼저 복구 시도 → 성공 시 다음 결제 가능
            const message = (error && error.message) ? String(error.message) : String(error);
            if (message.includes('pending payment')) {
              let pendingId = _payment && _payment.identifier;

              // 🎯 [Fix] SDK가 _payment를 null로 준 경우 sessionStorage에서 예비로 확인
              if (!pendingId) {
                const storedPayment = sessionStorage.getItem('pi-incomplete-payment');
                if (storedPayment) {
                  try {
                    const parsed = JSON.parse(storedPayment);
                    pendingId = parsed.identifier;
                    debugLog('[Payment] _payment가 없어서 sessionStorage에서 pendingId 추출 완료:', pendingId);
                  } catch (e) {
                    errorLog('[Payment] sessionStorage 파싱 실패', e);
                  }
                }
              }

              if (pendingId) {
                try {
                  debugLog('[Payment] Pending payment detected. Trying recoverPendingPayment first...', pendingId);
                  const recoverResult = await recoverPendingPayment(pendingId, environment);
                  if (recoverResult && recoverResult.recovered) {
                    debugLog('[Payment] Pending payment recovered (completed on server). User can retry.', pendingId);
                    sessionStorage.removeItem('pi-incomplete-payment'); // 복구 성공 시 제거
                    resolve({ paymentId: pendingId, recovered: true });
                    return;
                  }
                  debugLog('[Payment] Recover returned no_txid or failed. Trying cancelPayment...', pendingId);
                  await cancelPayment(pendingId, environment);
                  sessionStorage.removeItem('pi-incomplete-payment'); // 취소 성공 시 제거
                } catch (cleanupError) {
                  errorLog('[Payment] recoverPendingPayment/cancelPayment failed', cleanupError);
                }
              }
            }

            if (onError) onError(error);
            reject(error);
          }
        });
      } catch (e) {
        errorLog('[Payment] createPayment Exception', e);
        setIsProcessingPayment(false);
        if (onError) onError('create_payment_failed');
        reject(e);
      }
    });
  };

  // [추가] 티켓 구매 처리
  const processTicketPurchase = async (ticketAmount, piAmount, onSuccess, onError) => {
    debugLog('[Ticket] 티켓 구매 시작:', { ticketAmount, piAmount });

    try {
      if (isGuest()) {
        errorLog('[Ticket] 게스트는 티켓을 구매할 수 없습니다');
        onError('GUEST_RESTRICTED');
        return;
      }

      // Use the generic processPiPayment
      const result = await processPiPayment(
        piAmount,
        `${ticketAmount} 티켓 구매`,
        { type: 'ticket_purchase', ticketAmount: ticketAmount }
      );

      debugLog('[Ticket] 결제 완료:', result);

      // 티켓 수량 증가
      const newTicketCount = ticketCount + ticketAmount;
      setTicketCount(newTicketCount);
      localStorage.setItem(`painori-ticket-count-${environment}`, newTicketCount.toString());

      // 🔧 [결제 오류 수정] 티켓 구매 후 캐시 무효화
      const userId = sessionStorage.getItem('pi-user-id');
      const cacheKey = `subscription_${userId}_${environment}`;
      localStorage.removeItem(cacheKey);

      // subscriptionCache도 강제 무효화
      subscriptionCache.current.data = null;
      subscriptionCache.current.timestamp = 0;
      subscriptionCache.current.ttl = 0;

      // 🎯 [DB 동기화 제거] Cloud Function(purchaseTicket)에서 처리됨

      debugLog('[Ticket] 티켓 구매 후 캐시 무효화 완료');

      onSuccess(newTicketCount);
    } catch (error) {
      errorLog('[Ticket] 티켓 구매 실패:', error);
      onError(error.message || 'TICKET_PURCHASE_FAILED');
    }
  };

  // [추가] 광고 시청으로 티켓 획득
  const watchAdForTicket = async (onSuccess, onError) => {
    debugLog('[Ticket] 광고 시청으로 티켓 획득 시작');

    try {
      const skipAd = shouldRemoveAds();
      let adId = null;
      if (!skipAd) {
        const adResult = await PiAdapter.showAd('ad_ticket_reward');
        adId = adResult?.adId || null;
      }

      const userId = sessionStorage.getItem('pi-user-id') || piUser?.uid;
      if (!userId) throw new Error('User not authenticated');
      const apiUrls = getAPIUrls();
      const response = await fetch(apiUrls.verifyRewardedAd, {
        method: 'POST',
        headers: await bearerHeaders(),
        body: JSON.stringify({
          userId,
          adId,
          rewardType: 'ticket',
          environment,
          skipAd
        })
      });
      const verifyResult = await response.json().catch(() => ({}));
      if (!response.ok || !verifyResult.success) {
        throw new Error(verifyResult.error || 'AD_VERIFICATION_FAILED');
      }

      // 서버 트랜잭션에서 지급된 잔액만 로컬 상태에 반영
      const newTicketCount = Number(verifyResult.newBalance);
      if (!Number.isFinite(newTicketCount)) throw new Error('INVALID_TICKET_BALANCE');
      setTicketCount(newTicketCount);
      localStorage.setItem(`painori-ticket-count-${environment}`, newTicketCount.toString());

      debugLog('[Ticket] 광고 시청으로 티켓 획득 성공:', newTicketCount);
      onSuccess(newTicketCount);
    } catch (error) {
      errorLog('[Ticket] 광고 시청 실패:', error);
      onError(error.message || 'AD_WATCH_FAILED');
    }
  };

  // [수정] 광고 시청으로 STAR 포인트 획득 (24시간 쿨다운, 무제한)
  // -> 이제 watchAdForSpot으로 대체됨 (STAR 보상은 deprecated 될 예정이지만 코드는 유지)
  const watchAdForPpPoints = async (onSuccess, onError) => {
    // ... (Legacy implementation kept for reference or fallback if needed)
    // For now, we leave it as is, but UI calls watchAdForSpot
    warnLog('[AppContext] watchAdForPpPoints is deprecated. Use watchAdForSpot.');
    if (onError) onError('DEPRECATED_FEATURE');
  };

  // [Internal Helper] 광고 보상 처리 공통 함수 (Premium Skip 로직 포함)
  const handleAdReward = async (adUnitId, onReward) => {
    // 1. 프리미엄 구독자 체크 (광고 스킵)
    if (shouldRemoveAds()) {
      debugLog('[Ad] Premium user - Skipping ad');
      await onReward(true); // skipAd = true
      return;
    }

    // 2. 일반 사용자 - 광고 시청
    // 'ad_spot_reward' is a placeholder unit ID
    await PiAdapter.showAd(adUnitId);

    // 3. 광고 시청 완료 후 보상 지급
    await onReward(false); // skipAd = false
  };

  // [New] 광고 시청으로 SPOT 포인트 획득 (Refactored)
  // 🎯 상점 보상용 (24시간 쿨다운)
  const watchAdForSpot = async (onSuccess, onError) => {
    debugLog('[SPOT] 광고 보상 요청 시작 (상점)');
    try {
      const adId = 'ad_spot_reward'; // Placeholder ID

      // 공통 핸들러 사용
      await handleAdReward(adId, async (skipped) => {
        // 2. 서버 검증 및 지급 (verifyRewardedAd) — URL 사용 (함수 참조 아님)
        const userId = sessionStorage.getItem('pi-user-id') || piUser?.uid;
        if (!userId) throw new Error('User not authenticated');

        const apiUrls = getAPIUrls();
        const response = await fetch(apiUrls.verifyRewardedAd, {
          method: 'POST',
          headers: await bearerHeaders(),
          body: JSON.stringify({
            userId,
            adId: `ad_${Date.now()}`,
            rewardType: 'spot',
            environment,
            skipAd: skipped // 🎯 [New] 서버에 스킵 여부 전달 (검증 로직용)
          })
        });

        const result = await response.json();
        if (!result.success) throw new Error(result.message || 'Verification failed');

        // 3. 로컬 상태 업데이트
        const earnedAmount = await getAdWatchSpotRewards();
        setSpotPoints(prev => prev + earnedAmount);

        debugLog(`[SPOT] 보상 지급 완료 (${skipped ? 'Skipped' : 'Watched'}):`, earnedAmount);
        onSuccess(earnedAmount);
      });

    } catch (error) {
      errorLog('[SPOT] 광고 보상 실패:', error);
      const msg = error.message || 'AD_FAILED';

      // 🎯 서버 쿨다운(AD_COOLDOWN)과 동기화: 첫 호출에서라도 로컬 쿨다운 시작
      if (msg === 'COOLDOWN_ACTIVE') {
        try {
          const lastAdTimeKey = `painori-last-ad-time-${environment}`;
          localStorage.setItem(lastAdTimeKey, new Date().toISOString());
        } catch (e) {
          // localStorage 실패는 무시 (UI 표시용이므로)
        }
      }

      onError(msg);
    }
  };

  // [New] 선물상자 전용 광고 보상 획득 (매일 10회 제한, 쿨다운 없음)
  const watchAdForPointSpot = async (options = {}, onSuccess, onError) => {
    debugLog('[PointSpot] 광고 보상 요청 시작 (선물상자)');
    try {
      const adUnitId = 'ad_point_spot'; // 선물상자용 유닛 ID (DashBoard 설정 권장)

      // 1. 프리미엄/광고 상황에 맞춰 광고 시청 프로세스 진행
      let adId = 'premium_skip';
      if (!shouldRemoveAds()) {
        const adResult = await PiAdapter.showAd(adUnitId);
        adId = adResult?.adId || `ad_ps_${Date.now()}`;
      }

      // 2. 서버 검증 및 지급 (claimPointSpot 호출)
      const userId = sessionStorage.getItem('pi-user-id') || piUser?.uid;
      if (!userId) throw new Error('User not authenticated');

      // SpotService를 통해 백엔드 호출
      const type = options.isLandmarkPointSpot ? 'landmark' : 'ad';
      const result = await SpotService.claimPointSpot(type, adId, { uid: userId }, environment, options);

      if (!result.success) {
        throw new Error(result.message || 'Point spot claim failed');
      }

      // 3. 로컬 상태 업데이트 (SPOT 포인트)
      const earnedAmount = result.amount || 0.314;
      setSpotPoints(prev => prev + earnedAmount);

      debugLog(`[PointSpot] 보상 지급 완료:`, earnedAmount);
      onSuccess(earnedAmount);

    } catch (error) {
      errorLog('[PointSpot] 광고 보상 실패:', error);
      onError(error.message || 'AD_WATCH_FAILED');
    }
  };

  // [New] 랜드마크 방문을 위한 광고 시청 (표준화된 로직)
  const watchAdForLandmarkVisit = useCallback(async (onSuccess, onError) => {
    debugLog('[Landmark] 랜드마크 방문 광고 요청 시작');
    try {
      // 1. 프리미엄 구독자 체크 (광고 스킵)
      if (shouldRemoveAds()) {
        debugLog('[Landmark] Premium user - Skipping ad');
        onSuccess({ skipped: true });
        return;
      }

      // 2. 일반 사용자 - 광고 시청
      const adUnitId = 'ad_landmark_visit'; // Placeholder
      await PiAdapter.showAd(adUnitId);

      // 3. 광고 시청 완료 후 성공 콜백
      debugLog('[Landmark] 광고 시청 완료');
      onSuccess({ skipped: false });

    } catch (error) {
      errorLog('[Landmark] 광고 시청 실패:', error);
      if (onError) onError(error.message || 'AD_WATCH_FAILED');
    }
  }, [shouldRemoveAds]);

  // [향후 확장] 출석 체크로 STAR 포인트 획득 (준비된 구조)
  const claimAttendanceReward = useCallback(async (onSuccess, onError) => {
    debugLog('[STAR Points] 출석 체크 STAR 포인트 획득 시작');

    try {
      // 출석 체크 로직 (향후 구현)
      // const attendanceData = await checkAttendanceStatus();
      // const ppReward = calculateAttendanceReward(attendanceData);

      // 임시: 출석 체크 시 50 STAR 지급 (향후 동적 계산)
      const starEarned = getAdWatchPpPoints();
      addPpPoints(starEarned, 'attendance');

      debugLog('[STAR Points] 출석 체크 STAR 포인트 획득 성공:', starEarned);
      onSuccess(starEarned);
    } catch (error) {
      errorLog('[STAR Points] 출석 체크 실패:', error);
      onError(error.message || 'ATTENDANCE_FAILED');
    }
  }, [addPpPoints]);

  // [향후 확장] 이벤트 기반 STAR 포인트 획득 (준비된 구조)
  const claimEventReward = useCallback(async (eventType, rewardAmount, onSuccess, onError) => {
    debugLog('[STAR Points] 이벤트 STAR 포인트 획득 시작:', { eventType, rewardAmount });

    try {
      // 이벤트 검증 로직 (향후 구현)
      // const isValidEvent = await validateEvent(eventType);
      // if (!isValidEvent) throw new Error('Invalid event');

      addPpPoints(rewardAmount, `event_${eventType}`);

      debugLog('[STAR Points] 이벤트 STAR 포인트 획득 성공:', { eventType, rewardAmount });
      onSuccess(rewardAmount);
    } catch (error) {
      errorLog('[STAR Points] 이벤트 STAR 포인트 획득 실패:', error);
      onError(error.message || 'EVENT_REWARD_FAILED');
    }
  }, [addPpPoints]);

  // [추가] 티켓 사용 - 🎯 Server-First Strategy (STAR 포인트와 동일 패턴)
  const useTicket = async (amount = 1, source = 'game_use') => {
    const userId = sessionStorage.getItem('pi-user-id');

    // 1. 입력값 검증
    if (typeof amount !== 'number' || amount <= 0) {
      const error = new Error('Invalid amount');
      errorLog('[Ticket] 티켓 사용 실패 - 잘못된 값:', { amount, source });
      throw error;
    }

    // 2. 게스트 체크
    if (isGuest()) {
      const error = new Error('Guest users cannot use tickets');
      debugLog('[Ticket] 게스트는 티켓 사용 불가');
      throw error;
    }

    // 3. 중복 처리 방지 (Global Lock)
    if (window.isUsingTicket) {
      const error = new Error('TICKET_USE_IN_PROGRESS');
      warnLog('[Ticket] 티켓 사용 처리 중 - 중복 요청 차단');
      throw error;
    }

    if (userId) {
      window.isUsingTicket = true;

      try {
        debugLog('[Ticket] 티켓 사용 시작 - 서버 요청:', { amount, source });

        // 4. Cloud Function 호출 (서버 검증 및 처리)
        // 🎯 [Fix] AppContext.js - httpsCallable 대신 api.consumeTicket 사용 (onRequest 대응)
        const result = await consumeTicket({ uid: userId }, source, environment);

        /* [Legacy Code Removed]
        const consumeTicketCallable = httpsCallable(functions, 'consumeTicket');
        const result = await consumeTicketCallable({
          piUser: { uid: userId }, // 🎯 필수: 사용자 식별자
          reason: source,          // 🎯 필수: 사용 사유 (source -> reason)
          environment
        });
        */

        // 5. 성공 시에만 로컬 상태 업데이트
        if (result && result.success) {
          // 서버가 remainingTickets를 반환하면 사용하고, 없으면 로컬에서 -amount 계산 (방어 처리)
          const serverTickets = result.remainingTickets;
          const newTicketCount = (typeof serverTickets === 'number')
            ? serverTickets
            : Math.max(0, (ticketCount || 0) - amount);

          setTicketCount(newTicketCount);
          // [Fix] 올바른 LocalStorage 키 사용
          localStorage.setItem(`painori-ticket-count-${environment}`, newTicketCount.toString());

          debugLog('[Ticket] 티켓 사용 성공 - 서버 동기화 완료:', {
            used: amount,
            remaining: newTicketCount
          });

          // 이벤트 발생 (필요 시 UI 즉시 반응용)
          window.dispatchEvent(new CustomEvent('ticketUsed', {
            detail: { amount, remaining: newTicketCount }
          }));

          return true;
        } else {
          throw new Error(result?.error || 'Unknown server error');
        }

      } catch (err) {
        errorLog('[Ticket] 티켓 사용 실패:', err);
        // 서버가 INSUFFICIENT_TICKETS를 반환하면 로컬 캐시가 잘못된 것 → 0으로 강제 동기화
        if (err.message === 'INSUFFICIENT_TICKETS') {
          setTicketCount(0);
          localStorage.setItem(`painori-ticket-count-${environment}`, '0');
          warnLog('[Ticket] 로컬 캐시 불일치 감지 → 0으로 강제 동기화');
        }
        throw err;
      } finally {
        window.isUsingTicket = false;
      }
    }

    const error = new Error('User not authenticated');
    throw error;
  };


  // [추가] Pi Clash 게임 플레이 (일일 제한 및 티켓 소모)

  // 🎯 [신규] 해금된 그림팩 수 계산 함수 (최대 5개까지)
  const getUnlockedThemeCount = useCallback(() => {
    // 🔧 구매한 테마들만 계산 (downloadStatus는 다른 Context에서 관리)
    const purchasedThemeIds = Object.keys(purchasedThemes);

    // 무료 테마는 항상 해금된 것으로 간주 (명화 1)
    const freeThemes = ['masterpiece_1'];

    // 중복 제거하여 실제 해금된 테마 수 계산
    const unlockedThemes = new Set([...freeThemes, ...purchasedThemeIds]);
    const count = unlockedThemes.size;

    // 최대 5개까지만 적용
    const finalCount = Math.min(count, 5);
    debugLog('[AppContext] 해금된 그림팩 수:', { free: freeThemes.length, purchased: purchasedThemeIds.length, total: count, applied: finalCount });

    return Math.max(1, finalCount); // 최소 1배 보장
  }, [purchasedThemes]);

  // [통합] 구독 구매 처리 (STAR Payment Version)
  const processPiPaymentSubscription = async (subscriptionType, onSuccess = null, onError = null) => {
    debugLog('[Payment] STAR 구독 구매 시작:', subscriptionType);

    if (isGuest()) {
      errorLog('[Payment] 게스트는 구독 불가');
      if (onError) onError('guest_not_allowed');
      return;
    }

    if (subscriptionPurchaseInFlightRef.current) {
      throw new Error('PAYMENT_IN_PROGRESS');
    }
    subscriptionPurchaseInFlightRef.current = true;
    setIsProcessingPayment(true);

    try {
      const userId = sessionStorage.getItem('pi-user-id') || piUser?.uid;

      // 🎯 [Change] Use STAR Payment (purchaseSubscriptionWithStar API) — URL 사용 (함수 참조 아님)
      const apiUrls = getAPIUrls();
      const response = await fetch(apiUrls.purchaseSubscriptionWithStar, {
        method: 'POST',
        headers: await bearerHeaders(),
        body: JSON.stringify({
          userId,
          subscriptionType,
          environment
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Subscription purchase failed');
      }

      debugLog('[Payment] STAR 구독 구매 성공:', result);

      // 캐시 무효화 및 상태 즉시 갱신
      const cacheKey = `subscription_${userId}_${environment}`;
      localStorage.removeItem(cacheKey);
      subscriptionCache.current.data = null;
      subscriptionCache.current.timestamp = 0;

      await checkSubscriptionStatus(true); // 강제 갱신

      // UI 알림
      window.dispatchEvent(new CustomEvent('purchaseCompleted', {
        detail: { type: 'subscription', subscriptionType }
      }));

      playSound('purchase_success');

      if (onSuccess) onSuccess();
      return result;

    } catch (error) {
      errorLog('[Payment] STAR 구독 구매 실패:', error);
      // STAR 부족 에러 처리
      if (error.message.includes('INSUFFICIENT_BALANCE')) {
        if (onError) onError('INSUFFICIENT_BALANCE');
      } else {
        if (onError) onError('PAYMENT_FAILED');
      }
      throw error;
    } finally {
      subscriptionPurchaseInFlightRef.current = false;
      setIsProcessingPayment(false);
    }
  };

  const purchaseMyLandSlot = useCallback(async (userId, env, slot, type, user) => {
    const result = await purchaseMyLandSlotApi(userId, env, slot, type, user);

    // My Land 구매 직후 STAR/슬롯 상태를 즉시 반영한다.
    if (result && typeof result.ppPoints === 'number') {
      setPpPoints(result.ppPoints);
      localStorage.setItem(`painori-pp-points-${env}`, String(result.ppPoints));
    }
    if (result?.myLandSlots) {
      setMyLandSlots(result.myLandSlots);
    }

    return result;
  }, []);

  // [하위 호환성] 기존 함수들 유지 - deprecated
  const processPiPaymentAdFree = async (duration, onSuccess, onError) => {
    warnLog('[AppContext] processPiPaymentAdFree is deprecated. Use processPiPaymentSubscription instead.');
    const subscriptionType = duration === '24h' ? '1d' : '7d';
    return processPiPaymentSubscription(subscriptionType, onSuccess, onError);
  };

  const processPiPaymentVip = async (onSuccess, onError) => {
    warnLog('[AppContext] processPiPaymentVip is deprecated. Use processPiPaymentSubscription instead.');
    return processPiPaymentSubscription('30d', onSuccess, onError);
  };



  const isThemePurchased = (themeId) => {
    // 🎯 [수정] 서버 데이터 우선 확인 (실시간 반영)
    const serverData = subscriptionCache.current.data;
    if (serverData && serverData.purchasedThemes && serverData.purchasedThemes.includes(themeId)) {
      debugLog(`[AppContext] 테마 구매 확인 (서버) - ${themeId}: true`);
      return true;
    }

    // 🎯 [백업] 로컬 데이터 확인
    const result = purchasedThemes.hasOwnProperty(themeId);
    debugLog(`[AppContext] 테마 구매 확인 (로컬) - ${themeId}:`, result);
    return result;
  };

  const checkPaymentStatus = async (paymentId) => {
    debugLog('[Payment] 결제 상태 확인:', maskSensitiveData({ paymentId }).paymentId);
  };



  // [추가] 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (updateStatsTimerRef.current) {
        clearTimeout(updateStatsTimerRef.current);
      }
    };
  }, []);

  const value = {
    piUser, // 🎯 [추가] Pi 사용자 정보
    userId: piUser?.uid, // ✅ [Fix] AttendanceModal 400 에러 해결 - userId export
    isPaymentEnabled,
    isSoundEnabled,
    toggleSound,
    playSound,
    currentLanguage,
    changeLanguage,
    totalHonorScore,
    addHonorScore,
    getCurrentTier,
    processPiPayment,
    isThemePurchased,
    isProcessingPayment,
    purchasedThemes,
    checkPaymentStatus,
    environment, // 🎯 [Stage 2 핵심] 환경 정보 노출
    totalClearCount, // [수정] 총 클리어 횟수
    addClearCount, // [추가] 클리어 횟수 증가 함수
    currentThemePage,
    setCurrentThemePage,
    // [신규] 테마별 최고 점수 관련
    themeHighScores,
    updateThemeHighScore,
    getThemeHighScore,
    // [추가] 개발자 리셋 함수들
    resetPurchasedThemes,
    resetHonorScore,
    clearThemeProgress,
    resetAllGameData,
    resetGameStats, // [추가] Firebase 포함 게임 통계 리셋
    clearImageCache, // [추가] 그림팩 캐시 삭제
    // [통합] 구독 관련
    subscriptionStatus, // 새로운 통합 상태
    processPiPaymentSubscription, // 새로운 통합 구매 함수
    hasActiveSubscription, // 새로운 활성 확인 함수
    shouldRemoveAds, // 유지 (광고 제거 확인)
    getActiveSubscriptionBenefits, // 구독 혜택 조회
    checkSubscriptionStatus,
    resetSubscriptionStatus, // 새로운 리셋 함수
    // [하위 호환성] 기존 함수들 - deprecated
    adFreeStatus: subscriptionStatus, // 호환성을 위해 매핑
    vipStatus: subscriptionStatus && subscriptionStatus.type === '30d' ? subscriptionStatus : null,
    processPiPaymentAdFree,
    processPiPaymentVip,
    hasActiveVip,
    resetAdFreeStatus,
    resetVipStatus,
    // [추가] 게스트 관련
    isGuest, // 게스트 여부 확인 함수
    // [추가] 초기 동기화 에러 상태
    initialSyncError,
    // 🆕 [2025-12-19 추가] 테스트 모드 관련
    isTestMode, // 테스트 모드 상태
    canMakePayment, // 결제 가능 여부 (테스트 모드 고려)
    // [추가] 티켓 시스템 관련
    ticketCount, // 현재 티켓 수량
    setTicketCount, // 티켓 수량 설정 함수
    processTicketPurchase, // 티켓 구매 함수
    processTicketPurchase, // 티켓 구매 함수
    watchAdForTicket, // 광고 시청으로 티켓 획득
    watchAdForSpot, // [New] 광고 시청으로 SPOT 포인트 획득
    watchAdForPointSpot, // [New] 선물상자 전용 광고 보상
    watchAdForPpPoints, // (Deprecated)
    useTicket, // 티켓 사용 함수
    getUnlockedThemeCount, // 🎯 해금된 그림팩 수 계산 함수
    // [추가] PP 포인트 시스템 관련
    ppPoints, // 현재 PP 포인트
    setPpPoints, // [New] STAR 포인트 setter (CITY NFT 등 서버 응답 반영용)
    spotPoints, // [New] SPOT 포인트
    setSpotPoints, // [New] SPOT 포인트 setter
    addPpPoints, // PP 포인트 획득 함수 (Batched)
    rewardUser: addPpPoints, // 하위 호환성 유지
    spendPpPoints, // PP 포인트 소비 함수
    recoverPpPoints, // [추가] STAR 포인트 복구 함수
    syncGameStats, // 🎯 [근본적 해결] 게임 통계 동기화 함수
    // 🆕 [2026-01-29] 주간 랭킹 시스템 관련
    gloryPoints, // Glory Points (주간 랭킹 보상)
    weeklyScore, // 주간 점수
    claimAttendanceReward, // [향후 확장] 출석 체크 PP 획득
    claimEventReward, // [향후 확장] 이벤트 PP 획득
    // 🎯 [추가] 진동 관련
    isVibrationEnabled, // 진동 설정 상태
    toggleVibration, // 진동 토글 함수
    triggerVibration, // 진동 트리거 함수
    // [추가] 게임 상태 보존 관련
    saveGameState, // 게임 상태 저장 함수
    restoreGameState, // 게임 상태 복원 함수
    clearGameState, // 게임 상태 삭제 함수
    savedGpsPermission, // [추가] 저장된 GPS 권한 정보
    savedAgreements, // 🎯 [ToS] 저장된 약관 동의 정보
    isAuthInitializing, // 🎯 [근본 해결] 로그인 초기화 상태
    authState: contextAuthState, // 🎯 [Fix] 로그인 상태 (TosAgreementModal에서 사용)
    landmarkSpotRate, // 🎯 [Landmark v4.1] SPOT 지급률 (SPOT/분)
    landmarkOccupancyLimit, // 🎯 [Landmark v4.1] 점유 시간 제한 (시간)
    // 🎯 [AppMode] 리소스 관리 시스템 상태
    appMode,
    setAppMode,
    setHeavyTaskState,
    pointSpotInventory, // 🆕 선물상자 인벤토리 제공
    setPointSpotInventory,
    pointSpotOpenedToday, // 🆕 [GiftBox] 오늘 오픈 수 (UI 표시용)
    watchAdForLandmarkVisit, // 🎯 [Landmark v4.1] 랜드마크 방문 광고
    // My Land
    myLandSlots,
    myLandRegistrations,
    setMyLandRegistrations,
    purchaseMyLandSlot,
    registerMyLand,
    myLandCapture,
    renameMyLand
  };

  // 🔧 [V1.5] 이벤트 기반 동기화 초기화
  useEffect(() => {
    const cleanupSync = setupEventBasedSync();
    const cleanupFlush = setupImmediateFlushEvents();

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (cleanupSync) cleanupSync();
      if (cleanupFlush) cleanupFlush();

      // 메모리 누수 방지
      if (window.earningBatchTimer) {
        clearTimeout(window.earningBatchTimer);
        window.earningBatchTimer = null;
      }
      window.earningBatchQueue = [];
      window.isPurchasing = false;
    };
  }, [setupEventBasedSync, setupImmediateFlushEvents]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};